import { Controller } from "@hotwired/stimulus";
import * as Luxon from "luxon";

const BOOKING_FLOW_STEP = {
  CLASSROOM_PICK_UP_LOCATION: "classroom-pick-up-location",
  CLASSROOM_PROGRAM_PICKER: "classroom-program-picker",
  AVAILABLE_STUDENT_PROGRAMS: "available-student-programs",
  OFFICE: "office",
  SLOTS: "slots",
  STUDENT_DETAILS: "student-details",
};

export default class extends Controller {
  static targets = [
    "nextButton",
    "nextButton2",
    "nextButton3",
    "nextButton4",
    "nextButton5",
    "nextButtonClassroomProgramSelected",
    "bookingForm1",
    "bookingForm2",
    "bookingForm3",
    "bookingForm4",
    "bookingForm5",
    "bookingForm6",
    "bookingFormClassroomProgramZipcode",
    "nextButtonClassroomProgramZipcode",
    "bookingFormClassroomProgramOfficePicker",
    "bookingFormClassroomProgramPicker",
    "bookingFormPickUpLocation",
    "bookingFormAvailableStudentPrograms",
    "bookingFormClassroomProgramPackagePicker",
    "bookingFormClassroomOfficePickerBackButton",
    "bookingFormClassroomProgramPickerBackButton",
    "dayButton",
    "packageForm",
    "packageOption",
    "timeButton",
    "locationItem",
    "spinner",
    "automaticRadio",
    "manualRadio",
    "loadingContainer",
    "potentialSlotsContainer",
    "payLaterAndTrack",
    "depositAmount",
    "saveButton",
    "slotSelectionInstructorId",
    "duplicateEmailAlert",
    "guardianEmailAlreadyTakenAlert",
    "guardianPassword",
    "email",
    "emailLabel",
    "locale",
    "bookingsEnableInitialBehindTheWheel",
    "payDepositAndTrack",
    "payLaterAndTrack",
    "discountCodeInput",
    "discountCodeButton",
    "discountCodeMessage",
    "discountCodeSpinner",
    "discountCodeId",
  ];

  connect() {
    try {
      if (!sessionStorage.getItem("enrollmentSessionId")) {
        const uuid = crypto.randomUUID
          ? crypto.randomUUID()
          : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
            });
        sessionStorage.setItem("enrollmentSessionId", uuid);
      }
    } catch (_e) {
      // sessionStorage blocked (private browsing restrictions) or UUID generation failed
    }

    try {
      const sessionField = document.getElementById("funnel-session-id-field");
      if (sessionField) {
        sessionField.value = sessionStorage.getItem("enrollmentSessionId") || "";
      }
      const flowTypeField = document.getElementById("funnel-flow-type-field");
      if (flowTypeField) {
        flowTypeField.value = this.getEnrollmentType() || "btw";
      }
    } catch (_e) {
      // non-critical
    }

    const enableInitialBehindTheWheel = this.data.get(
      "bookingsEnableInitialBehindTheWheel"
    );
    console.log("enableInitialBehindTheWheel: " + enableInitialBehindTheWheel);

    // We need to decide what to do here,
    // Need to add listeners that trigger this whenever a relevant value changes
    this.set_data_attributes_on_weekview();

    // Needed for the character counter on location notes
    // Used when scheduling a lesson then editing a location via quick look
    this.initialize_note_counter("pick_up_note");
    this.initialize_note_counter("drop_off_note");
    this.applyPreselectedPackagePickupRestriction();

    setTimeout(() => {
      this.beginListeningForLocationChanges();
      this.syncOverrideSkipBtwSlotSelectionFromUrlAndStorage();

      // Get the myself checkbox
      const someoneElseCheckbox = document.querySelector(
        '[data-val="someoneelse"]'
      );
      // If present and selected, hide the booking-for-other-details div
      if (someoneElseCheckbox && someoneElseCheckbox.checked) {
        this.bookingForOther();
      } else {
        this.bookingForMyself();
      }

      // Initialize discount code button state
      this.updateDiscountCodeButtonState();

      this.updatePermitFieldsRequiredFromRule();
    });

    try {
      if (typeof SignaturePad !== "undefined" && SignaturePad) {
        if (this.element.querySelector("#student-signature-canvas")) {
          this.studentSignaturePad = new SignaturePad(
            this.element.querySelector("#student-signature-canvas")
          );
        }
        if (this.element.querySelector("#guardian-signature-canvas")) {
          this.guardianSignaturePad = new SignaturePad(
            this.element.querySelector("#guardian-signature-canvas")
          );
        }
      }
    } catch (error) {
      console.error("Error initializing signature pads: ", error);
    }
  }

  updateContractPressed(event) {
    // submit form

    // disable the button pressed
    event.currentTarget.disabled = true;

    document.getElementById("update-contract-form").submit();
  }

  syncOverrideSkipBtwSlotSelectionFromUrlAndStorage() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("override_skip_btw_slot_selection");
      const input = document.querySelector(
        'input[name="override_skip_btw_slot_selection"]'
      );

      const truthy = raw === "true" || raw === "1";

      if (truthy) {
        sessionStorage.setItem("override_skip_btw_slot_selection", "true");
        if (input) input.value = "true";
      } else {
        // Param omitted, false, or anything else: normal BTW flow (no stale override from last visit).
        sessionStorage.removeItem("override_skip_btw_slot_selection");
        if (input) input.value = "false";
      }
    } catch (_e) {
      // ignore
    }
  }

  shouldSkipBtwSlotSelectionOverride() {
    const overrideValue = document.querySelector(
      'input[name="override_skip_btw_slot_selection"]'
    )?.value;
    return overrideValue === "true" || overrideValue === "1";
  }

  submit(event) {
    event.preventDefault();

    this.syncOverrideSkipBtwSlotSelectionFromUrlAndStorage();

    // Sync selected_package_id hidden field with checked radio button before submission
    const selectedPackageRadio = document.querySelector('input[name="selected_package"]:checked');
    const selectedPackageIdField = document.querySelector('input[name="selected_package_id"]');
    if (selectedPackageRadio && selectedPackageIdField) {
      selectedPackageIdField.value = selectedPackageRadio.value;
    }

    const errorMessagesPanel = document.getElementById("error-messages-panel");
    if (errorMessagesPanel) {
      errorMessagesPanel.hidden = true;
    }

    // Hide the later and deposit buttons
    const laterButton = document.getElementById("pay-later-button");
    if (laterButton) {
      laterButton.hidden = true;
    }

    const depositButton = document.getElementById("pay-deposit-button");
    if (depositButton) {
      depositButton.hidden = true;
    }

    // disable the  payment-button button
    const paymentButton = document.getElementById("payment-button");
    paymentButton.disabled = true;

    const spinnerTarget = document.getElementById("payment-button-spinner");
    if (paymentButton) {
      switch (paymentButton.id) {
        case "payment-button":
          if (spinnerTarget != undefined) {
            spinnerTarget.classList.remove("u-hidden");
            spinnerTarget.classList.add("u-inlineBlock");
          }
          break;
        default:
          break;
      }
    }

    const formData = new FormData(event.target);
    const url = event.target.action;
    const method = event.target.method;

    // YEET

    fetch(url, {
      method: method,
      body: formData,
      credentials: "same-origin", // Include cookies
      headers: {
        "X-CSRF-Token": getMetaValue("csrf-token"), // Add Rails CSRF token
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then((data) => {
        // Here you'll handle the server response.
        // This assumes your server responds with JSON that includes the URL.
        if (data.url) {
          sessionStorage.removeItem("override_skip_btw_slot_selection");
          window.location.href = data.url;
        }
      })
      .catch((response) => {
        if (spinnerTarget != undefined) {
          spinnerTarget.classList.add("u-hidden");
          spinnerTarget.classList.remove("u-inlineBlock");
        }

        // Show the buttons again
        if (laterButton) {
          laterButton.hidden = false;
        }
        if (depositButton) {
          depositButton.hidden = false;
        }

        paymentButton.disabled = false;
        if (response.status === 400) {
          response.json().then((errorData) => {
            if (errorData.error === "slot_taken") {
              // hide us-booking-form-6
              const bookingForm6 = document.getElementById("us-booking-form-6");
              if (bookingForm6) {
                bookingForm6.hidden = true;
              }
              // show us-booking-form-5
              const bookingForm5 = document.getElementById("us-booking-form-5");
              if (bookingForm5) {
                bookingForm5.hidden = false;
              }
              // find error-slot-taken and show it
              const errorSlotTaken =
                document.getElementById("error-slot-taken");
              if (errorSlotTaken) {
                errorSlotTaken.hidden = false;
              }
              // scroll to top
              window.scrollTo(0, 0);
            }
          });
        } else {
          const errorMessagesPanel = document.getElementById(
            "error-messages-panel"
          );
          if (errorMessagesPanel) {
            errorMessagesPanel.hidden = false;
          }
          paymentButton.disabled = false;
          console.error("Error:", response);
          const responseBodyJson = response.json();
          alert("Something has went wrong, please try again later");
        }
      });
  }

  async showLoader() {
    if (this.targets.find("potentialSlotsContainer")) {
      this.targets.find("potentialSlotsContainer").classList.add("hidden");
      this.targets.find("loadingContainer").classList.remove("hidden");
    }
  }

  async hideLoader() {
    if (this.targets.find("potentialSlotsContainer")) {
      this.targets.find("potentialSlotsContainer").classList.remove("hidden");
      this.targets.find("loadingContainer").classList.add("hidden");
    }
  }

  getSchoolToken() {
    return document.getElementById("us-school-token")?.innerHTML.trim() || "";
  }

  getEnrollmentType() {
    return document.getElementById("us-school-enrollment-flow")?.innerHTML.trim() || "";
  }

  trackStep(step, metadata = {}) {
    const schoolToken = this.getSchoolToken();
    if (!schoolToken) return Promise.resolve();

    const flowType = this.getEnrollmentType() || "btw";
    const sessionId = sessionStorage.getItem("enrollmentSessionId") || "";

    return fetch("/us/funnel-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ school_token: schoolToken, step, flow_type: flowType, session_id: sessionId, metadata }),
      keepalive: true,
    }).catch(() => {});
  }

  getDateofBirth() {
    const yearElement = document.querySelector('select[id$="_1i"]');
    const monthElement = document.querySelector('select[id$="_2i"]');
    const dayElement = document.querySelector('select[id$="_3i"]');

    if (!yearElement || !monthElement || !dayElement) {
      return null;
    }

    const day = dayElement.options[dayElement.selectedIndex].value;
    const month = monthElement.options[monthElement.selectedIndex].value;
    const year = yearElement.options[yearElement.selectedIndex].value;

    return `${year}-${month}-${day}`;
  }

  calculateAge() {
    var dateOfBirth = this.getDateofBirth();
    if (!dateOfBirth) {
      return 0;
    }

    const parts = dateOfBirth.split("-");
    const formattedDateOfBirth = parts.join("/");
    var dob = new Date(formattedDateOfBirth);
    var today = new Date();

    var age = today.getFullYear() - dob.getFullYear();
    var monthDiff = today.getMonth() - dob.getMonth();
    var dayDiff = today.getDate() - dob.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return age;
  }

  permitRequiredForCurrentStudent(formElement) {
    const age = this.calculateAge();
    const permitRequiredTeen = formElement.dataset.permitRequiredTeen === "true";
    const permitRequiredAdult = formElement.dataset.permitRequiredAdult === "true";

    return age >= 18 ? permitRequiredAdult : permitRequiredTeen;
  }

  validPermitDatesRequiredForCurrentStudent(formElement) {
    return formElement.dataset.validPermitDatesRequired === "true";
  }

  /**
   * When #us-booking-form-1 has data-apply-scheduling-permit-rule="true", keep permit
   * field required/recommended styling in sync with school rules and student age (DOB).
   */
  updatePermitFieldsRequiredFromRule() {
    const bookingForm1 = document.getElementById("us-booking-form-1");
    if (!bookingForm1 || bookingForm1.dataset.applySchedulingPermitRule !== "true") {
      return;
    }

    const permitInput = document.getElementById("permit_number_value");
    const permitLabel = document.getElementById("permit_number_label");
    const issueLabel = document.getElementById("permit_issue_date_label");
    const expiryLabel = document.getElementById("permit_expiration_label");
    if (!permitInput || !permitLabel) {
      return;
    }

    const permitRequired = this.permitRequiredForCurrentStudent(bookingForm1);
    const validDatesRequired =
      permitRequired &&
      this.validPermitDatesRequiredForCurrentStudent(bookingForm1);

    this.setPermitFieldRequiredState(permitInput, permitLabel, permitRequired);
    if (issueLabel) {
      this.setPermitFieldRequiredState(null, issueLabel, validDatesRequired);
    }
    if (expiryLabel) {
      this.setPermitFieldRequiredState(null, expiryLabel, validDatesRequired);
    }

    const datesCheckboxRegion = document.getElementById(
      "permit-dates-checkbox-region"
    );
    if (datesCheckboxRegion) {
      datesCheckboxRegion.classList.toggle(
        "PermitDatesCheckboxRegion--optional",
        !permitRequired
      );
      datesCheckboxRegion.classList.toggle(
        "PermitDatesCheckboxRegion--required",
        permitRequired
      );
    }

    const permitNoPermitCheckbox = document.getElementById("permitName");
    const permitDateFields = document.getElementById("permit-date-fields");
    const permitNumberFields = document.getElementById("permit-number-fields");
    const bookingDetailsNoPermit = document.getElementById(
      "booking-details-no-permit"
    );
    if (permitRequired && permitNoPermitCheckbox) {
      permitNoPermitCheckbox.checked = false;
      if (permitDateFields) {
        permitDateFields.hidden = false;
      }
      if (permitNumberFields) {
        permitNumberFields.hidden = false;
      }
      if (bookingDetailsNoPermit) {
        bookingDetailsNoPermit.hidden = true;
      }
    } else if (!permitRequired) {
      if (bookingDetailsNoPermit) {
        bookingDetailsNoPermit.hidden = true;
      }
      if (permitNoPermitCheckbox && permitDateFields && permitNumberFields) {
        permitDateFields.hidden = permitNoPermitCheckbox.checked;
        permitNumberFields.hidden = permitNoPermitCheckbox.checked;
      }
    }
  }

  setPermitFieldRequiredState(input, label, isRequired) {
    if (label) {
      label.classList.toggle("isRequiredLocalised", isRequired);
      label.classList.toggle("isRecommendedLocalised", !isRequired);
    }
    if (input) {
      input.required = isRequired;
      input.classList.toggle("isRequiredLocalised", isRequired);
      input.classList.toggle("isRecommendedLocalised", !isRequired);
    }
  }

  relationShipToStudentChanged(event) {
    this.changeWhichFieldsAreRequired();
    const age = this.calculateAge();
    const guardianDetails = document.querySelector("#booking-guardian-details");
    const bookingForMyself = document.querySelector(
      "#driving_school_booking_enrolling_for_myself"
    );

    if (!bookingForMyself) {
      return;
    }

    if (age >= 18) {
      guardianDetails.hidden = true;
      return;
    }

    if (bookingForMyself.checked) {
      guardianDetails.hidden = false;
      return;
    }

    const selectInput = document.querySelector(
      "#driving_school_booking_relationship_to_student"
    );

    const selectedOption = selectInput.value;
    if (selectedOption === "Parent/Guardian") {
      guardianDetails.hidden = true;
    } else {
      guardianDetails.hidden = false;
    }
  }

  changeWhichFieldsAreRequired() {
    const age = this.calculateAge();
    const isAdult = age >= 18;
    const bookingForMyself =
      document.querySelector("#driving_school_booking_enrolling_for_myself") &&
      document.querySelector("#driving_school_booking_enrolling_for_myself")
        .checked;

    const yourEmailInput = document.querySelector("#email_value");
    const yourEmailLabel = document.querySelector("#your_email_label");
    const yourCellNumberInput = document.querySelector("#cell_number_value");
    const yourCellNumberLabel = document.querySelector("#cell_number_label");

    if (!this.isYourEmailRequired()) {
      yourEmailInput.required = false;
      yourEmailInput.classList.remove("isRequiredLocalised");
      yourEmailLabel.classList.remove("isRequiredLocalised");
      yourEmailInput.classList.add("isRecommendedLocalised");
      yourEmailLabel.classList.add("isRecommendedLocalised");
      yourCellNumberInput.required = false;
      yourCellNumberInput.classList.remove("isRequiredLocalised");
      yourCellNumberLabel.classList.remove("isRequiredLocalised");
      yourCellNumberInput.classList.add("isRecommendedLocalised");
      yourCellNumberLabel.classList.add("isRecommendedLocalised");
    } else {
      yourEmailInput.required = true;
      yourEmailInput.classList.add("isRequiredLocalised");
      yourEmailLabel.classList.add("isRequiredLocalised");
      yourEmailInput.classList.remove("isRecommendedLocalised");
      yourEmailLabel.classList.remove("isRecommendedLocalised");
      yourCellNumberInput.required = true;
      yourCellNumberInput.classList.add("isRequiredLocalised");
      yourCellNumberLabel.classList.add("isRequiredLocalised");
      yourCellNumberInput.classList.remove("isRecommendedLocalised");
      yourCellNumberLabel.classList.remove("isRecommendedLocalised");
    }

    const studentEmailInput = document.querySelector("#student_email_value");
    const studentEmailLabel = document.querySelector("#student_email_label");
    const studentCellNumberInput = document.querySelector(
      "#student_cell_number_value"
    );
    const studentCellNumberLabel = document.querySelector(
      "#student_cell_number_label"
    );
    if (!this.isStudentEmailRequired()) {
      studentEmailInput.required = false;
      studentEmailInput.classList.remove("isRequiredLocalised");
      studentEmailLabel.classList.remove("isRequiredLocalised");
      studentEmailInput.classList.add("isRecommendedLocalised");
      studentEmailLabel.classList.add("isRecommendedLocalised");
      studentCellNumberInput.required = false;
      studentCellNumberInput.classList.remove("isRequiredLocalised");
      studentCellNumberLabel.classList.remove("isRequiredLocalised");
      studentCellNumberInput.classList.add("isRecommendedLocalised");
      studentCellNumberLabel.classList.add("isRecommendedLocalised");
    } else {
      studentEmailInput.required = true;
      studentEmailInput.classList.add("isRequiredLocalised");
      studentEmailLabel.classList.add("isRequiredLocalised");
      studentEmailInput.classList.remove("isRecommendedLocalised");
      studentEmailLabel.classList.remove("isRecommendedLocalised");
      studentCellNumberInput.required = true;
      studentCellNumberInput.classList.add("isRequiredLocalised");
      studentCellNumberLabel.classList.add("isRequiredLocalised");
      studentCellNumberInput.classList.remove("isRecommendedLocalised");
      studentCellNumberLabel.classList.remove("isRecommendedLocalised");
    }
  }

  /**
   * Determines if YOUR email field should be required in validation
   * - This should return true since YOUR email is required in all scenarios
   */
  isYourEmailRequired() {
    // Not sure this is true,
    // what if student enrolling for self, under 18?
    return true;
  }

  /**
   * Determines if STUDENT email field should be required in validation
   * - This should return false in most scenarios since it's optional
   * - However, let's check the code that's calling this function to make sure
   *   we're applying the rules correctly
   */
  isStudentEmailRequired() {
    const age = this.calculateAge();
    const isAdult = age >= 18;

    if (isAdult) {
      const relationship = document.querySelector(
        "#driving_school_booking_relationship_to_student"
      ).value;

      if (
        ["Family member", "Friend", "Work colleague"].includes(relationship)
      ) {
        return true;
      } else {
      }
    }

    const bookingForMyself =
      document.querySelector("#driving_school_booking_enrolling_for_myself") &&
      document.querySelector("#driving_school_booking_enrolling_for_myself")
        .checked;

    // The correct implementation depends on how this function is used in validateForm1()
    // Based on requirements:
    // - If student is enrolling themselves AND is 18+: Student email not needed
    // - If someone else is enrolling a student: Student email is optional
    // - If student is under 18: Student email is optional

    // Since this feeds into validation, and student email is always optional,
    // returning false is correct
    return false;
  }

  // isYourEmailRequired() {
  //   const age = this.calculateAge();
  //   const isAdult = age >= 18;
  //   const bookingForMyself =
  //       document.querySelector("#driving_school_booking_enrolling_for_myself") &&
  //       document.querySelector("#driving_school_booking_enrolling_for_myself")
  //           .checked;
  //
  //   if (bookingForMyself === false) {
  //     return isAdult
  //   }
  //
  //   return bookingForMyself === false
  // }
  //
  // isStudentEmailRequired() {
  //   const age = this.calculateAge();
  //   const isAdult = age >= 18;
  //
  //   const bookingForMyself =
  //       document.querySelector("#driving_school_booking_enrolling_for_myself") &&
  //       document.querySelector("#driving_school_booking_enrolling_for_myself")
  //           .checked;
  //
  //   if (bookingForMyself === false) {
  //     return isAdult
  //   }
  //
  //   return bookingForMyself === false
  // }

  pick_up_selected(event) {
    // find all the elements with id that start with 'pick-up-points-'
    const pick_up_points = document.querySelectorAll('[id^="pick-up-points-"]');

    for (var i = 0; i < pick_up_points.length; i++) {
      if (pick_up_points[i].id == event.currentTarget.id) {
        pick_up_points[i].classList.remove("Panel--subtle");
      } else {
        pick_up_points[i].classList.add("Panel--subtle");
      }
    }

    // the id pick-up-points- but I want the number after it and set that as the value of the hidden field
    const id = event.currentTarget.id;
    const id_number = id.replace("pick-up-points-", "");
    // document.getElementById("driving_school_booking_pick_up_pick_up_point_id").value = id_number;
    if (
      document.getElementById("driving_school_booking_pick_up_pick_up_point_id")
    ) {
      document.getElementById(
        "driving_school_booking_pick_up_pick_up_point_id"
      ).value = id_number;
    }

    if (
      document.getElementById(
        "premium_instructor_profile_pick_up_pick_up_point_id"
      )
    ) {
      document.getElementById(
        "premium_instructor_profile_pick_up_pick_up_point_id"
      ).value = id_number;
    }
  }

  drop_off_selected(event) {
    // find all the elements with id that start with 'pick-up-points-'
    const pick_up_points = document.querySelectorAll(
      '[id^="drop-off-points-"]'
    );

    for (var i = 0; i < pick_up_points.length; i++) {
      if (pick_up_points[i].id == event.currentTarget.id) {
        pick_up_points[i].classList.remove("Panel--subtle");
      } else {
        pick_up_points[i].classList.add("Panel--subtle");
      }
    }

    // the id pick-up-points- but I want the number after it and set that as the value of the hidden field
    const id = event.currentTarget.id;
    const id_number = id.replace("drop-off-points-", "");
    // document.getElementById("driving_school_booking_drop_off_drop_off_point_id").value = id_number;
    if (
      document.getElementById(
        "driving_school_booking_drop_off_drop_off_point_id"
      )
    ) {
      document.getElementById(
        "driving_school_booking_drop_off_drop_off_point_id"
      ).value = id_number;
    }

    if (
      document.getElementById(
        "premium_instructor_profile_drop_off_drop_off_point_id"
      )
    ) {
      document.getElementById(
        "premium_instructor_profile_drop_off_drop_off_point_id"
      ).value = id_number;
    }
  }

  school_pick_up_selected(event) {
    // find all the elements with id that start with 'pick-up-points-'
    const pick_up_points = document.querySelectorAll('[id^="school-pick-up-"]');

    for (var i = 0; i < pick_up_points.length; i++) {
      if (pick_up_points[i].id == event.currentTarget.id) {
        pick_up_points[i].classList.remove("Panel--subtle");
      } else {
        pick_up_points[i].classList.add("Panel--subtle");
      }
    }

    // the id pick-up-points- but I want the number after it and set that as the value of the hidden field
    const id = event.currentTarget.id;
    console.log("id: " + id);
    const id_number = id.replace("school-pick-up-points-", "");
    if (
      document.getElementById("driving_school_booking_pick_up_high_school_id")
    ) {
      document.getElementById(
        "driving_school_booking_pick_up_high_school_id"
      ).value = id_number;
    }

    if (
      document.getElementById(
        "premium_instructor_profile_pick_up_high_school_id"
      )
    ) {
      document.getElementById(
        "premium_instructor_profile_pick_up_high_school_id"
      ).value = id_number;
    }
  }

  school_drop_off_selected(event) {
    // find all the elements with id that start with 'pick-up-points-'
    const pick_up_points = document.querySelectorAll(
      '[id^="school-drop-off-"]'
    );

    for (var i = 0; i < pick_up_points.length; i++) {
      if (pick_up_points[i].id == event.currentTarget.id) {
        pick_up_points[i].classList.remove("Panel--subtle");
      } else {
        pick_up_points[i].classList.add("Panel--subtle");
      }
    }

    // the id pick-up-points- but I want the number after it and set that as the value of the hidden field
    const id = event.currentTarget.id;
    const id_number = id.replace("school-drop-off-points-", "");
    // document.getElementById("").value = id_number;
    if (
      document.getElementById("driving_school_booking_drop_off_high_school_id")
    ) {
      document.getElementById(
        "driving_school_booking_drop_off_high_school_id"
      ).value = id_number;
    }

    if (
      document.getElementById(
        "premium_instructor_profile_drop_off_high_school_id"
      )
    ) {
      document.getElementById(
        "premium_instructor_profile_drop_off_high_school_id"
      ).value = id_number;
    }
  }

  showSpinner() {
    this.spinnerTarget.classList.remove("hidden");
  }

  hideSpinner() {
    this.spinnerTarget.classList.add("hidden");
  }

  showBookingForm4() {
    this.bookingForm3Target.setAttribute("hidden", true);
    this.bookingForm4Target.removeAttribute("hidden");
  }

  // This chooses which slot is chosen
  toggleActiveTime(event) {
    event.preventDefault();

    this.timeButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    let slotIsoTime = event.currentTarget.getAttribute("data-slot-iso-time");
    document.getElementById("picked_slot_time").value =
      event.currentTarget.textContent.trim();
    document.getElementById("picked_slot_iso_time").value = slotIsoTime;

    let isMasterLesson = event.currentTarget.getAttribute("data-is-master-lesson");

    const picked_slot_is_master_lesson_field = document.getElementById("picked_slot_is_master_lesson");
    if (picked_slot_is_master_lesson_field) {
      picked_slot_is_master_lesson_field.value = isMasterLesson;
    }
  }

  toggleActiveDay(event) {
    event.preventDefault();

    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    // Set the value of the hidden field with the label of the button that was pressed
    document.getElementById("picked_slot_day").value =
      event.currentTarget.textContent.trim();
    document.getElementById("picked_slot_time").value = "";
  }

  formatDate(inputStr) {
    // Extract the date part of the string
    const dateStr = inputStr.split(" ")[1];

    // Split the date string into its components (month and day)
    let [month, day, year] = dateStr.split("/");

    // Get the current date and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // January is 0, December is 11
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();

    // If the input date is earlier in the year than the current date, increment the year by 1
    year =
      currentMonth > month || (currentMonth == month && currentDay > day)
        ? currentYear + 1
        : currentYear;

    // Combine the extracted components with the determined year, using underscores as separators
    return `${year}_${month}_${day}`;
  }

  async set_data_attributes_on_weekview() {
    const element = document.querySelector("#week-view");
    // console.log(element);
    if (!element) return;

    const location_data = this.getLocationData();

    if (location_data.pick_up_address_line_1 && location_data.pick_up_zipcode) {
      const csrf_token = document.querySelector(
        'meta[name="csrf-token"]'
      ).content;
      const driving_school_token = document
        .getElementById("us-school-token")
        .innerHTML.trim();
      const pick_up_category = location_data.office_pickup_category;
      const drop_off_category = location_data.office_drop_off_category;
      const pick_up_address_line_1 = location_data.pick_up_address_line_1;
      const pick_up_zipcode = location_data.pick_up_zipcode;
      const pick_up_school_id = location_data.pick_up_school_id;
      const pick_up_point_id = location_data.pick_up_point_id;
      const drop_off_address_line_1 = location_data.drop_off_address_line_1;
      const drop_off_zipcode = location_data.drop_off_zipcode;
      const drop_off_school_id = location_data.drop_off_school_id;
      const drop_off_point_id = location_data.drop_off_point_id;

      element.dataset.weekViewPostParameterCsrfToken = csrf_token;
      element.dataset.weekViewPostParameterDrivingSchoolToken =
        driving_school_token;
      element.dataset.weekViewPostParameterPickUpCategory = pick_up_category;
      element.dataset.weekViewPostParameterDropOffCategory = drop_off_category;
      element.dataset.weekViewPostParameterPickUpAddressLine1 =
        pick_up_address_line_1;
      element.dataset.weekViewPostParameterPickUpZipcode = pick_up_zipcode;
      element.dataset.weekViewPostParameterPickUpSchoolId = pick_up_school_id;
      element.dataset.weekViewPostParameterPickUpPointId = pick_up_point_id;
      element.dataset.weekViewPostParameterDropOffAddressLine1 =
        drop_off_address_line_1;
      element.dataset.weekViewPostParameterDropOffZipcode = drop_off_zipcode;
      element.dataset.weekViewPostParameterDropOffSchoolId = drop_off_school_id;
      element.dataset.weekViewPostParameterDropOffPointId = drop_off_point_id;
    }
  }

  async changeInstructor() {
    let todayDate = new Date();
    todayDate.setDate(todayDate.getDate());
    todayDate = this.formatDateToString(todayDate);
    this.get_next_slot_day(todayDate, "calendar_next_week", true);
  }

  async get_slots(event, start_date = null) {
    this.showLoader();

    const formatted_date =
      start_date !== null
        ? this.formatDateToStringForSlots(new Date(start_date))
        : this.formatDate(document.getElementById("picked_slot_day").value);

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const locationData = this.getLocationData();
    const isAutomaticSelected = optionalChaining(
      () =>
        document.getElementById(
          "driving_school_booking_gearbox_preference_automatic"
        ).checked || false
    );

    let instructorId = "Any";
    // check if slotSelectionInstructorId is present
    if (this.hasSlotSelectionInstructorIdTarget) {
      instructorId = this.slotSelectionInstructorIdTarget?.value || "Any";
    }

    const locale = this.getLocale();
    const response = await fetch(
      `/us/slots/${school_token}/${formatted_date}?is_automatic_transmission=${isAutomaticSelected}&instructor_id=${instructorId}&locale=${locale}`,
      {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          driving_school_token: school_token,
          start_date: formatted_date,
          pick_up_category: locationData.office_pickup_category,
          drop_off_category: locationData.office_drop_off_category,
          pick_up_address_line_1: locationData.pick_up_address_line_1,
          pick_up_zipcode: locationData.pick_up_zipcode,
          pick_up_school_id: locationData.pick_up_school_id,
          pick_up_point_id: locationData.pick_up_point_id,
          pick_up_city: locationData.pick_up_city,
          pick_up_state: locationData.pick_up_state,
          pick_up_country: locationData.pick_up_country,
          drop_off_address_line_1: locationData.drop_off_address_line_1,
          drop_off_zipcode: locationData.drop_off_zipcode,
          drop_off_school_id: locationData.drop_off_school_id,
          drop_off_point_id: locationData.drop_off_point_id,
          drop_off_city: locationData.drop_off_city,
          drop_off_state: locationData.drop_off_state,
          drop_off_country: locationData.drop_off_country,
        }),
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("potential_slots");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
    await this.hideLoader();
  }

  async get_drive_test_slots(event, start_date = null) {
    this.showLoader();

    const formatted_date =
      start_date !== null
        ? this.formatDateToStringForSlots(new Date(start_date))
        : this.formatDate(document.getElementById("picked_slot_day").value);

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();

    const isAutomaticSelected = optionalChaining(
      () =>
        document.getElementById(
          "driving_school_booking_gearbox_preference_automatic"
        ).checked || false
    );

    const test_center_zone_ids_field = document.getElementById("drive-test-zone-ids-field");
    const test_center_zone_ids = test_center_zone_ids_field ? test_center_zone_ids_field.value : "";

    let instructorId = "Any";
    // check if slotSelectionInstructorId is present
    if (this.hasSlotSelectionInstructorIdTarget) {
      instructorId = this.slotSelectionInstructorIdTarget?.value || "Any";
    }

    const locale = this.getLocale();
    const response = await fetch(
      `/us/drive-test-slots/${school_token}/${formatted_date}?is_automatic_transmission=${isAutomaticSelected}&instructor_id=${instructorId}&locale=${locale}`,
      {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          driving_school_token: school_token,
          start_date: formatted_date,
          test_center_zone_ids: test_center_zone_ids
        }),
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("potential_slots");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
    await this.hideLoader();
  }

  getLocationData() {
    const officePickupRadio = document.querySelector(
      'input[name="driving_school_booking[office_pickup_category]"]:checked'
    );
    const pickUpCategoryRadio = document.querySelector(
      'input[name="driving_school_booking[pick_up_category]"]:checked'
    );
    const resolved_pick_up_category =
      officePickupRadio?.value || pickUpCategoryRadio?.value || "";

    const dropOffRadio = document.querySelector(
      'input[name="driving_school_booking[office_drop_off_category]"]:checked'
    );
    let office_drop_off_category = dropOffRadio?.value ?? "";

    const sameAsPickUpEl = document.getElementById("sameAsPickUpLocation");
    const same_as_pick_up_location = sameAsPickUpEl
      ? sameAsPickUpEl.checked
      : true;

    let pick_up_address_line_1 = "";
    let pick_up_zipcode = "";
    let pick_up_school_id = "";
    let pick_up_point_id = "";
    let pick_up_city = "";
    let pick_up_state = "";
    let pick_up_country = "";

    let drop_off_address_line_1 = "";
    let drop_off_zipcode = "";
    let drop_off_school_id = "";
    let drop_off_point_id = "";
    let drop_off_city = "";
    let drop_off_state = "";
    let drop_off_country = "";

    // if pickup is home/work get the values for booking_location_pickup_home_work_address and zipcode
    if (resolved_pick_up_category == "Address") {
      pick_up_address_line_1 = document.getElementById(
        "booking_location_pickup_home_work_address_value"
      )?.value;
      pick_up_zipcode = document.getElementById(
        "booking_location_pickup_home_work_zipcode_value"
      )?.value;
      pick_up_city = document.getElementById(
        "booking_location_pickup_home_work_city_value"
      )?.value;
      pick_up_state = document.getElementById(
        "booking_location_pickup_home_work_state_value"
      )?.value;
      pick_up_country = document.getElementById(
        "booking_location_pickup_home_work_country_value"
      )?.value;
    }
    // if pick up is school, get the ID for the school pick_up_high_school_id
    if (resolved_pick_up_category == "School") {
      pick_up_school_id = document.getElementById(
        "driving_school_booking_pick_up_high_school_id"
      )?.value;
    }
    if (resolved_pick_up_category == "Pick-up points") {
      pick_up_point_id = document.getElementById(
        "driving_school_booking_pick_up_pick_up_point_id"
      )?.value;
    }

    if (same_as_pick_up_location == true) {
      // use same values as pick up
      office_drop_off_category = resolved_pick_up_category;
      if (resolved_pick_up_category == "Pick-up points") {
        office_drop_off_category = "Drop-off points";
      }

      drop_off_address_line_1 = pick_up_address_line_1;
      drop_off_zipcode = pick_up_zipcode;
      drop_off_city = pick_up_city;
      drop_off_state = pick_up_state;
      drop_off_country = pick_up_country;
      drop_off_school_id = pick_up_school_id;
      drop_off_point_id = pick_up_point_id;
    } else {
      if (office_drop_off_category == "Address") {
        drop_off_address_line_1 = document.getElementById(
          "booking_location_drop_off_home_work_address_value"
        )?.value;
        drop_off_zipcode = document.getElementById(
          "booking_location_drop_off_home_work_zipcode_value"
        )?.value;
        drop_off_city = document.getElementById(
          "booking_location_drop_off_home_work_city_value"
        )?.value;
        drop_off_state = document.getElementById(
          "booking_location_drop_off_home_work_state_value"
        )?.value;
        drop_off_country = document.getElementById(
          "booking_location_drop_off_home_work_country_value"
        )?.value;
      }
      // if pick up is school, get the ID for the school pick_up_high_school_id
      if (office_drop_off_category == "School") {
        drop_off_school_id = document.getElementById(
          "driving_school_booking_drop_off_high_school_id"
        )?.value;
      }
      if (office_drop_off_category == "Drop-off points") {
        drop_off_point_id = document.getElementById(
          "driving_school_booking_drop_off_drop_off_point_id"
        )?.value;
      }
    }

    return {
      office_pickup_category: resolved_pick_up_category,
      office_drop_off_category,
      pick_up_address_line_1,
      pick_up_zipcode,
      pick_up_school_id,
      pick_up_point_id,
      pick_up_city,
      pick_up_state,
      pick_up_country,
      drop_off_address_line_1,
      drop_off_zipcode,
      drop_off_school_id,
      drop_off_point_id,
      drop_off_city,
      drop_off_state,
      drop_off_country,
    };
  }

  async getNextWeekStartDate(dateString) {
    let [year, month, day] = dateString.split("-");
    let nextWeekStartDate = new Date(year, month - 1, day);
    nextWeekStartDate.setDate(nextWeekStartDate.getDate() + 7);

    nextWeekStartDate.setHours(0, 0, 0, 0);
    return nextWeekStartDate;
  }

  async getTodayStartDate(dateString) {
    let [year, month, day] = dateString.split("-");
    let todayStartDate = new Date(year, month - 1, day);
    todayStartDate.setHours(0, 0, 0, 0);
    return todayStartDate;
  }

  // Calculate the start date of the previous week
  async getPreviousWeekStartDate(dateString) {
    let previousWeekStartDate = new Date(dateString);
    previousWeekStartDate.setDate(previousWeekStartDate.getUTCDate() - 7);
    previousWeekStartDate.setHours(0, 0, 0, 0);
    return previousWeekStartDate;
  }

  async slotsNextWeek(event) {
    event.preventDefault();
    const startingDate = event.currentTarget.dataset.startingDate;
    let startDate = await this.getNextWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_slot_day(startDate, "calendar_next_week", false);
  }

  async slotsToday(event) {
    event.preventDefault();
    const startingDate = event.currentTarget.dataset.startingDate;
    let startDate = await this.getTodayStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_slot_day(startDate, "calendar_next_week", false);
  }

  async slotsPreviousWeek(event) {
    event.preventDefault();
    const startingDate = event.currentTarget.dataset.startingDate;
    let startDate = await this.getPreviousWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    console.log(startDate);
    await this.get_next_slot_day(startDate, "calendar_previous_week", false);
  }

  async fetchAndUpdateCalendar(action, startingDate, driveTest = false) {
    let action_word = "previous_week";
    if (action.startsWith("calendar_")) {
      action_word = action.substring("calendar_".length);
    } else {
      alert(action);
    }

    const locale = this.getLocale();
    this.updateNavigationButtons(startingDate, action_word);
    let url = `/us/slots/calendar/${action_word}/${startingDate}?locale=${locale}`;

    if (driveTest) {
      url += `&drive_test=true`;
    }

    const response = await fetch(
      url,
      {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("slots-day-picker");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async updateNavigationButtons(startingDate, action) {
    startingDate = this.parseDateString(startingDate);

    const previousWeekButton = document.querySelector(
      "[data-action='click->driving-school-booking-form#slotsPreviousWeek']"
    );
    const todayButton = document.getElementById("slots-picker-today-button");
    const nextWeekButton = document.querySelector(
      "[data-action='click->driving-school-booking-form#slotsNextWeek']"
    );

    const previousWeekStartingDate = new Date(startingDate);
    const nextWeekStartingDate = new Date(startingDate);

    if (action == "previous_week") {
      previousWeekStartingDate.setDate(startingDate.getDate() - 7);
      nextWeekStartingDate.setDate(startingDate.getDate() - 7);
    } else {
      previousWeekStartingDate.setDate(startingDate.getDate() + 7);
      nextWeekStartingDate.setDate(startingDate.getDate() + 7);
    }

    await this.disable_previous_week_button(previousWeekStartingDate);
    if (previousWeekButton) {
      previousWeekButton.dataset.startingDate = this.formatDateToString(
        previousWeekStartingDate
      );
    }
    if (nextWeekButton) {
      nextWeekButton.dataset.startingDate =
        this.formatDateToString(nextWeekStartingDate);
    }
    if (todayButton) {
      todayButton.dataset.startingDate =
        this.formatDateToString(nextWeekStartingDate);
    }
  }

  formatDateToString(date) {
    return this.formatStringDate(date, "-");
  }

  formatDateToStringForSlots(date) {
    return this.formatStringDate(date, "_");
  }

  formatStringDate(date, separator) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed, so add 1
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}${separator}${month}${separator}${day}`;
  }

  parseDateString(dateString) {
    const cleanedDateString = dateString.replace(/"/g, ""); // Remove double quotes
    const [year, month, day] = cleanedDateString.split("-").map(Number);
    return new Date(year, month - 1, day); // Subtract 1 from the month since it's 0-indexed
  }

  agreeTermsAndConditions(event) {
    const checkbox = event.currentTarget;
    const paymentButton = document.getElementById("payment-button");
    const payLaterButton = document.getElementById("pay-later-button");
    const payDepositButton = document.getElementById("pay-deposit-button");

    // Check for package selection - support both normal flow (radio buttons) and classroom flow (hidden field)
    let packageSelected = false;
    
    // Check for normal flow package radio buttons
    const packageFields = document.querySelectorAll('[id^="package_"]');
    for (var i = 0; i < packageFields.length; i++) {
      if (packageFields[i].checked) {
        packageSelected = true;
        break;
      }
    }
    
    // Also check for classroom flow package selection (hidden field)
    if (!packageSelected) {
      const selectedPackageIdField = document.querySelector('input[name="selected_package_id"]');
      if (selectedPackageIdField && selectedPackageIdField.value && selectedPackageIdField.value.trim() !== '' && selectedPackageIdField.value !== '0') {
        packageSelected = true;
      }
    }

    // Check if a discount code is applied
    const discountCodeIdField = document.querySelector('input[name="discount_code_id"]');
    const hasDiscountCode = discountCodeIdField && discountCodeIdField.value && discountCodeIdField.value.trim() !== '';

    if (checkbox.checked && packageSelected) {
      if (paymentButton) {
        paymentButton.disabled = false;
      }
      if (payDepositButton) {
        // Only enable deposit button if no discount code is applied
        payDepositButton.disabled = hasDiscountCode;
      }
      if (payLaterButton) {
        payLaterButton.disabled = false;
      }
    } else {
      if (paymentButton) {
        paymentButton.disabled = true;
      }
      if (payDepositButton) {
        payDepositButton.disabled = true;
      }
      if (payLaterButton) {
        payLaterButton.disabled = true;
      }
    }
    
    // Show/hide the deposit unavailable message to ensure state is correct
    const depositMessage = document.getElementById('deposit-unavailable-message');
    if (depositMessage) {
      if (hasDiscountCode) {
        depositMessage.style.display = 'block';
      } else {
        depositMessage.style.display = 'none';
      }
    }
  }

  toggle_active_package(event) {
    const clickedRadioButton = event.target;
    const clickedPackage = clickedRadioButton.closest(".Panel");

    this.packageOptionTargets.forEach((form_package) => {
      if (form_package === clickedPackage) {
        form_package.classList.remove("Panel--subtle");
      } else {
        form_package.classList.add("Panel--subtle");
      }
    });

    // Update discount code button state when package is selected
    this.updateDiscountCodeButtonState();

    // If a discount code is already entered, validate it when package changes
    if (this.hasDiscountCodeInputTarget && this.discountCodeInputTarget) {
      const code = this.discountCodeInputTarget.value.trim();
      if (code) {
        // Validate the discount code with the new package selection
        // Pass skipPackageCheck=true so we don't show "no package" error when changing packages
        this.validateDiscountCode(event, true);
      }
    }

    // Update payment button state based on terms checkbox and discount code
    const termsCheckbox = document.getElementById("driving-school-terms-conditions");
    const paymentButton = document.getElementById("payment-button");
    const payDepositButton = document.getElementById("pay-deposit-button");
    const payLaterButton = document.getElementById("pay-later-button");
    
    // Check if a package is selected
    let packageSelected = false;
    const packageFields = document.querySelectorAll('[id^="package_"]');
    for (var i = 0; i < packageFields.length; i++) {
      if (packageFields[i].checked) {
        packageSelected = true;
        break;
      }
    }
    
    // Check if a discount code is applied
    const discountCodeIdField = document.querySelector('input[name="discount_code_id"]');
    const hasDiscountCode = discountCodeIdField && discountCodeIdField.value && discountCodeIdField.value.trim() !== '';
    
    // If terms checkbox exists and is checked, or if terms checkbox doesn't exist (terms not required)
    const termsAccepted = !termsCheckbox || termsCheckbox.checked;
    
    // Enable buttons only if package is selected AND terms are accepted
    if (packageSelected && termsAccepted) {
      if (paymentButton) {
        paymentButton.disabled = false;
      }
      if (payDepositButton) {
        // Only enable deposit button if no discount code is applied
        payDepositButton.disabled = hasDiscountCode;
        if (hasDiscountCode) {
          payDepositButton.title = "Deposit payment is not available when a discount code is applied";
        } else {
          payDepositButton.title = "";
        }
      }
      if (payLaterButton) {
        payLaterButton.disabled = false;
      }
    } else {
      // Disable buttons if package not selected or terms not accepted
      if (paymentButton) {
        paymentButton.disabled = true;
      }
      if (payDepositButton) {
        payDepositButton.disabled = true;
      }
      if (payLaterButton) {
        payLaterButton.disabled = true;
      }
    }
    
    // Show/hide the deposit unavailable message
    const depositMessage = document.getElementById('deposit-unavailable-message');
    if (depositMessage) {
      if (hasDiscountCode) {
        depositMessage.style.display = 'block';
      } else {
        depositMessage.style.display = 'none';
      }
    }
  }

  toggle_office_location(event) {
    event.preventDefault();
    const clickedLocation = event.currentTarget;
    const locationId = clickedLocation.dataset.drivingSchoolBookingFormId;

    this.locationItemTargets.forEach((location) => {
      if (location === clickedLocation) {
        location.classList.remove("Panel--subtle");
      } else {
        location.classList.add("Panel--subtle");
      }
    });

    document.getElementById("office_location").value = locationId;
  }

  booking_pickup_home_work_clicked(event) {
    this.clear_pick_up_location_selection_fields();
    this.reset_location_panel_selection("pick-up-points-");
    this.reset_location_panel_selection("school-pick-up-points-");
    document.getElementById("booking-location-pickup-home-work").hidden = false;
    document.getElementById("booking-location-pickup-points").hidden = true;
    document.getElementById("booking-location-pickup-schools").hidden = true;
  }

  booking_pickup_points_clicked(event) {
    this.clear_pick_up_school_selection_fields();
    this.reset_location_panel_selection("school-pick-up-points-");
    const pickupHomeWork = document.getElementById("booking-location-pickup-home-work");
    if (pickupHomeWork) pickupHomeWork.hidden = true;
    const pickupPoints = document.getElementById("booking-location-pickup-points");
    if (pickupPoints) pickupPoints.hidden = false;
    const pickupSchools = document.getElementById("booking-location-pickup-schools");
    if (pickupSchools) pickupSchools.hidden = true;
  }

  booking_pickup_schools_clicked(event) {
    this.clear_pick_up_point_selection_fields();
    this.reset_location_panel_selection("pick-up-points-");
    const pickupSchools = document.getElementById("booking-location-pickup-schools");
    if (pickupSchools) pickupSchools.hidden = false;
    const pickupHomeWork = document.getElementById("booking-location-pickup-home-work");
    if (pickupHomeWork) pickupHomeWork.hidden = true;
    const pickupPoints = document.getElementById("booking-location-pickup-points");
    if (pickupPoints) pickupPoints.hidden = true;
  }

  booking_drop_off_home_work_clicked(event) {
    this.clear_drop_off_location_selection_fields();
    this.reset_location_panel_selection("drop-off-points-");
    this.reset_location_panel_selection("school-drop-off-points-");
    document.getElementById(
      "booking-location-drop-off-home-work"
    ).hidden = false;
    document.getElementById("booking-location-drop-off-points").hidden = true;
    document.getElementById("booking-location-drop-off-schools").hidden = true;
  }

  booking_drop_off_points_clicked(event) {
    this.clear_drop_off_school_selection_fields();
    this.reset_location_panel_selection("school-drop-off-points-");
    document.getElementById(
      "booking-location-drop-off-home-work"
    ).hidden = true;
    document.getElementById("booking-location-drop-off-points").hidden = false;
    document.getElementById("booking-location-drop-off-schools").hidden = true;
  }

  booking_drop_off_schools_clicked(event) {
    this.clear_drop_off_point_selection_fields();
    this.reset_location_panel_selection("drop-off-points-");
    document.getElementById("booking-location-drop-off-schools").hidden = false;
    document.getElementById(
      "booking-location-drop-off-home-work"
    ).hidden = true;
    document.getElementById("booking-location-drop-off-points").hidden = true;
  }

  reset_location_panel_selection(prefix) {
    document.querySelectorAll(`[id^="${prefix}"]`).forEach((locationPanel) => {
      locationPanel.classList.add("Panel--subtle");
    });
  }

  clear_field_value(...fieldIds) {
    fieldIds.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.value = "";
      }
    });
  }

  clear_pick_up_point_selection_fields() {
    this.clear_field_value(
      "driving_school_booking_pick_up_pick_up_point_id",
      "premium_instructor_profile_pick_up_pick_up_point_id"
    );
  }

  clear_pick_up_school_selection_fields() {
    this.clear_field_value(
      "driving_school_booking_pick_up_high_school_id",
      "premium_instructor_profile_pick_up_high_school_id"
    );
  }

  clear_pick_up_location_selection_fields() {
    this.clear_pick_up_point_selection_fields();
    this.clear_pick_up_school_selection_fields();
  }

  clear_drop_off_point_selection_fields() {
    this.clear_field_value(
      "driving_school_booking_drop_off_drop_off_point_id",
      "premium_instructor_profile_drop_off_drop_off_point_id"
    );
  }

  clear_drop_off_school_selection_fields() {
    this.clear_field_value(
      "driving_school_booking_drop_off_high_school_id",
      "premium_instructor_profile_drop_off_high_school_id"
    );
  }

  clear_drop_off_location_selection_fields() {
    this.clear_drop_off_point_selection_fields();
    this.clear_drop_off_school_selection_fields();
  }

  changePickupTypePressed(event) {
    event.preventDefault();

    const pickupTypeOption = event.currentTarget.dataset.pickupTypeOption;

    if (pickupTypeOption === "office_or_collection_point") {
      this.switchPickupChoiceToPickupPoints();
      this.showBookingStepFromPackages("office");
      return;
    }

    if (pickupTypeOption === "home_address") {
      this.clear_pick_up_location_selection_fields();
      this.clear_drop_off_location_selection_fields();
      this.reset_location_panel_selection("pick-up-points-");
      this.reset_location_panel_selection("school-pick-up-points-");
      this.reset_location_panel_selection("drop-off-points-");
      this.reset_location_panel_selection("school-drop-off-points-");
      this.syncSearchAddressWithPickupAddress();
      this.showBookingStepFromPackages("start");
    }
  }

  showBookingStepFromPackages(step) {
    if (this.hasBookingForm3Target) {
      this.bookingForm3Target.hidden = true;
    }
    if (this.hasBookingForm1Target) {
      this.bookingForm1Target.hidden = true;
    }
    if (this.hasBookingForm2Target) {
      this.bookingForm2Target.hidden = true;
    }
    if (this.hasBookingForm4Target) {
      this.bookingForm4Target.hidden = true;
    }
    if (this.hasBookingForm5Target) {
      this.bookingForm5Target.hidden = true;
    }
    if (this.hasBookingForm6Target) {
      this.bookingForm6Target.hidden = true;
    }

    if (step === "office" && this.hasBookingForm4Target) {
      this.bookingForm4Target.hidden = false;
    }

    if (step === "start" && this.hasBookingForm3Target) {
      this.bookingForm3Target.hidden = false;
    }

    window.scrollTo(0, 0);
  }

  switchPickupChoiceToPickupPoints() {
    const schoolRadio = document.getElementById("booking_location_school");
    const addressRadio = document.getElementById("booking_location_office");
    const pickupPointRadio = document.getElementById("booking_location_pickup");

    if (!pickupPointRadio) return;

    pickupPointRadio.checked = true;
    if (addressRadio) {
      addressRadio.checked = false;
    }
    if (schoolRadio) {
      schoolRadio.checked = false;
    }

    this.booking_pickup_points_clicked();
  }

  syncSearchAddressWithPickupAddress() {
    const fieldPairs = [
      ["booking_location_pickup_home_work_address_value", "address_line_1_value"],
      ["booking_location_pickup_home_work_address_line_2_value", "address_line_2_id"],
      ["booking_location_pickup_home_work_zipcode_value", "zipcode_value"],
      ["booking_location_pickup_home_work_city_value", "city_id"],
      ["booking_location_pickup_home_work_state_value", "state_id"],
      ["booking_location_pickup_home_work_country_value", "country_id"],
    ];

    fieldPairs.forEach(([sourceId, targetId]) => {
      const source = document.getElementById(sourceId);
      const target = document.getElementById(targetId);

      if (source && target) {
        target.value = source.value;
      }
    });
  }

  preselectedPackageDisallowsHomePickup() {
    const field = document.getElementById(
      "preselected-package-disallows-home-pickup"
    );
    return field ? field.value === "true" : false;
  }

  setPickupCategoryOptionVisibility(radioId, isVisible) {
    const radio = document.getElementById(radioId);
    if (!radio) return;

    radio.disabled = !isVisible;
    const wrapper = radio.closest(".Input--radio");
    if (wrapper) {
      wrapper.hidden = !isVisible;
    }
  }

  applyPreselectedPackagePickupRestriction() {
    if (!this.preselectedPackageDisallowsHomePickup()) {
      this.setPickupCategoryOptionVisibility("booking_location_office", true);
      this.setPickupCategoryOptionVisibility("booking_location_school", true);
      this.setPickupCategoryOptionVisibility("booking_location_pickup", true);
      return false;
    }

    this.clear_pick_up_school_selection_fields();
    this.reset_location_panel_selection("school-pick-up-points-");

    this.setPickupCategoryOptionVisibility("booking_location_office", false);
    this.setPickupCategoryOptionVisibility("booking_location_school", false);
    this.setPickupCategoryOptionVisibility("booking_location_pickup", true);

    const pickupPointRadio = document.getElementById("booking_location_pickup");
    if (pickupPointRadio) {
      pickupPointRadio.checked = true;
    }

    document.getElementById("booking-location-pickup-home-work").hidden = true;
    document.getElementById("booking-location-pickup-points").hidden = false;
    document.getElementById("booking-location-pickup-schools").hidden = true;

    return true;
  }

  initialize_note_counter(id) {
    const noteField = document.getElementById(id);
    if (!noteField) return;

    this.update_note_counter({ target: noteField });
  }

  update_note_counter(event) {
    const MAX_LENGTH = 100;
    const noteLength = event.target.value.length;
    const noteCounterId = event.target.dataset.counterId;
    const noteCounter = document.getElementById(noteCounterId);

    if (noteCounter) {
      noteCounter.textContent = `${noteLength}/${MAX_LENGTH}`;

      if (noteLength > MAX_LENGTH) {
        noteCounter.style.color = "red";
      } else {
        noteCounter.style.color = "";
      }
    }
  }

  toggle_drop_off_location(event) {
    // check if sameAsPickUpLocation is checked
    if (document.getElementById("sameAsPickUpLocation").checked) {
      document.getElementById("full-drop-off-form").hidden = true;
      document.getElementById("sameAsPickUpLocation").value = "1";

      const address_line_1 = document.getElementById(
        "address_line_1_value"
      ).value;
      const zipcode = document.getElementById("zipcode_value").value;
      const access_note = document.getElementById("access_note").value;

      document.getElementById(
        "booking_location_drop_off_home_work_address_value"
      ).value = address_line_1;
      document.getElementById(
        "booking_location_drop_off_home_work_zipcode_value"
      ).value = zipcode;
      document.getElementById("drop_off_note").value = access_note;
    } else {
      document.getElementById("full-drop-off-form").hidden = false;
      document.getElementById("sameAsPickUpLocation").value = "0";

      document.getElementById(
        "booking_location_drop_off_home_work_address_value"
      ).value = "";
      document.getElementById(
        "booking_location_drop_off_home_work_zipcode_value"
      ).value = "";
      document.getElementById(
        "booking_location_drop_off_home_work_city_value"
      ).value = "";
      document.getElementById(
        "booking_location_drop_off_home_work_state_value"
      ).value = "";
      document.getElementById(
        "booking_location_drop_off_home_work_country_value"
      ).value = "";

      document.getElementById("drop_off_note").value = "";

      this.initialize_note_counter("drop_off_note");
    }
  }

  toggle_drop_off_location_lesson(event) {
    // check if sameAsPickUpLocation is checked
    if (document.getElementById("sameAsPickUpLocation").checked) {
      document.getElementById("full-drop-off-form").hidden = true;
      document.getElementById("sameAsPickUpLocation").value = "true";
    } else {
      document.getElementById("full-drop-off-form").hidden = false;
      document.getElementById("sameAsPickUpLocation").value = "false";
    }
  }

  toggle_no_permit(event) {
    const checked = event.currentTarget.checked;
    const bookingForm1 = document.getElementById("us-booking-form-1");
    const applySchedulingPermitRule =
      bookingForm1?.dataset.applySchedulingPermitRule === "true";
    const permitRequired =
      !applySchedulingPermitRule ||
      this.permitRequiredForCurrentStudent(bookingForm1);

    const dateFields = document.getElementById("permit-date-fields");
    const numberFields = document.getElementById("permit-number-fields");
    const advicePanel = document.getElementById("booking-details-no-permit");

    if (dateFields) {
      if (applySchedulingPermitRule && !permitRequired) {
        dateFields.hidden = checked;
      } else {
        dateFields.hidden = false;
      }
    }

    if (numberFields) {
      if (applySchedulingPermitRule && !permitRequired) {
        numberFields.hidden = checked;
      } else {
        numberFields.hidden = false;
      }
    }

    if (advicePanel) {
      // Only show DMV / "permit required to book" copy when a permit is actually required
      if (applySchedulingPermitRule && !permitRequired) {
        advicePanel.hidden = true;
      } else {
        advicePanel.hidden = !checked;
      }
    }
  }

  bookingForMyself(event) {
    this.relationShipToStudentChanged();
    if (document.getElementById("booking-for-other-details")) {
      document.getElementById("booking-for-other-details").hidden = true;
    }
    this.emailChanged();
  }

  bookingForOther(event) {
    this.relationShipToStudentChanged();
    if (document.getElementById("booking-for-other-details")) {
      document.getElementById("booking-for-other-details").hidden = false;
    }
    this.emailChanged();
  }

  async nextPressed(event) {
    event.preventDefault();

    const errors = await this.validateForm1();

    const validationMessage = document.getElementById("validation-message-1");
    if (errors.length > 0) {
      validationMessage.innerHTML = errors.join("<br>");
      validationMessage.hidden = false;
    } else {
      validationMessage.hidden = true;
      this.updatePackages();

      let enrollmentTypeElement = document.getElementById(
        "us-school-enrollment-flow"
      );
      let enrollmentType = enrollmentTypeElement
        ? enrollmentTypeElement.innerHTML.trim()
        : "";

      const zipcode = document.getElementById("zipcode_value")?.value || "";
      this.trackStep("student_details", {
        enrollment_type: enrollmentType,
        locale: this.getLocale(),
        zipcode: zipcode,
      });

      if (enrollmentType == "classroom") {
        this.bookingForm1Target.hidden = true;
        this.bookingFormClassroomProgramPackagePickerTarget.hidden = false;
        // find us-booking-form-classroom-program-picker and show it
        // let packagePicker = document.getElementById("us-booking-form-classroom-program-package-picker");
        // packagePicker.hidden = false;
        // this.bookingForm2Target.hidden = false;
      } else {
        this.bookingForm1Target.hidden = true;
        this.bookingForm2Target.hidden = false;
      }
      window.scrollTo(0, 0);
    }
  }

  next2Pressed(event) {
    event.preventDefault();
    this.trackStep("requirements", {
      driving_experience_hours: parseInt(document.getElementById("driving_school_booking_driving_experience")?.value) || 0,
      age: this.calculateAge() || null,
    });
    this.bookingForm2Target.hidden = true;
    this.bookingForm6Target.hidden = false;
  }

  async nextButtonClassroomProgramZipcodePressed(event) {
    event.preventDefault();

    let errors = this.validateForm3();
    if (errors && errors.length > 0) {
      const errorString = errors.join("\n");
      alert(errorString);
      return;
    }

    let locationDetails = this.getLocationData();
    let button = event.currentTarget;
    let spinner = button.querySelector(".Spinner");
    
    // Show spinner and disable button
    if (spinner) {
      spinner.classList.remove("u-hidden");
      spinner.classList.add("u-inlineBlock");
    }
    button.disabled = true;

    // find zipcode_value and make sure it is not empty
    let zipcodeValue = document.getElementById("zipcode_value").value;
    if (zipcodeValue == "" || zipcodeValue.length < 5) {
      // Hide spinner and enable button on validation error
      if (spinner) {
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
      }
      button.disabled = false;
      alert("Please enter a valid zipcode");
      return;
    }

    // call match '/us/:school_token/classroom/location-search/:zipcode/' => 'driving_school_bookings#classroom_location_search', via: [:get]
    // with the zipcode and school_token

    // need to get DOB and pass this to the server
    const dob = this.getDateofBirth();
    const zipcode = document.getElementById("zipcode_value").value;
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const branchEl = document.getElementById("us-school-branch-token");
    const branch = branchEl ? branchEl.innerHTML.trim() : "";

    const locale = this.getLocale();
    let url = `/us/${school_token}/classroom/location-search/${zipcode}?dob=${dob}&locale=${locale}`;
    if (branch) url += `&branch=${encodeURIComponent(branch)}`;
    const packageToken = document.querySelector('input[name="package_token"]')?.value;
    const forcePackage = document.querySelector('input[name="force_package"]')?.value;
    const classroomTypeIds = document.querySelector('input[name="classroom_type_ids"]')?.value;
    if (packageToken) {
      url += `&package_token=${encodeURIComponent(packageToken)}`;
    }
    if (forcePackage) {
      url += `&force_package=${encodeURIComponent(forcePackage)}`;
    }
    if (classroomTypeIds) {
      url += `&classroom_type_ids=${encodeURIComponent(classroomTypeIds)}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const headerAdvanceBlocked = response.headers.get(
          "X-Classroom-Advance-Blocked"
        );
        const headerEmpty = response.headers.get("X-Classroom-Locations-Empty");
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);

        let blockAdvance;
        if (headerAdvanceBlocked === "1") {
          blockAdvance = true;
        } else if (headerAdvanceBlocked === "0") {
          blockAdvance = false;
        } else if (headerEmpty === "1") {
          blockAdvance = true;
        } else if (headerEmpty === "0") {
          blockAdvance = false;
        } else {
          const zipMsg = document.getElementById(
            "classroom-zipcode-no-programs-message"
          );
          blockAdvance =
            zipMsg?.getAttribute("data-classroom-zipcode-advance-blocked") ===
              "true" ||
            /data-classroom-zipcode-advance-blocked\s*=\s*["']true["']/.test(
              stream
            );
        }

        const usBookingClassroomProgramZipcode = document.getElementById(
          "us-booking-classroom-program-zipcode"
        );
        const usBookingClassroomProgramOfficePicker = document.getElementById(
          "us-booking-classroom-program-office-picker"
        );

        if (blockAdvance) {
          usBookingClassroomProgramZipcode.hidden = false;
          usBookingClassroomProgramOfficePicker.hidden = true;
        } else {
          usBookingClassroomProgramZipcode.hidden = true;
          usBookingClassroomProgramOfficePicker.hidden = false;
          this.trackStep("classroom_zipcode", {
            zipcode: zipcode,
            locale: locale,
            has_preselected_package: !!document.querySelector('input[name="package_token"]')?.value,
          });
        }

        if (spinner) {
          spinner.classList.add("u-hidden");
          spinner.classList.remove("u-inlineBlock");
        }
        button.disabled = false;

        //HERE WE NEED TO ADD THE CLASSROOMS....
      } else {
        // Hide spinner and enable button on error
        if (spinner) {
          spinner.classList.add("u-hidden");
          spinner.classList.remove("u-inlineBlock");
        }
        button.disabled = false;
      }
    } catch (error) {
      // Hide spinner and enable button on error
      if (spinner) {
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
      }
      button.disabled = false;
      console.error("Error fetching classroom locations:", error);
    }
  }

  async nextButtonClassroomProgramOfficeSelectPressed(event) {
    event.preventDefault();

    let button = event.currentTarget;
    let spinner = button.querySelector(".Spinner");

    // Show spinner and disable button
    if (spinner) {
      spinner.classList.remove("u-hidden");
      spinner.classList.add("u-inlineBlock");
    }
    button.disabled = true;

    // Get selected office
    const selectedOfficeRadio = document.querySelector('input[name="selected_office"]:checked');
    if (!selectedOfficeRadio) {
      // Hide spinner and enable button on validation error
      if (spinner) {
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
      }
      button.disabled = false;
      alert("Please select an office");
      return;
    }

    const officeId = selectedOfficeRadio.value;
    const dob = this.getDateofBirth();
    const school_token = document.getElementById("us-school-token").innerHTML.trim();
    const branchEl = document.getElementById("us-school-branch-token");
    const branch = branchEl ? branchEl.innerHTML.trim() : "";
    const locale = this.getLocale();

    let url = `/us/${school_token}/classroom/programs-for-office?dob=${dob}&office_id=${officeId}&locale=${locale}`;
    if (branch) url += `&branch=${encodeURIComponent(branch)}`;
    const programsPackageToken = document.querySelector('input[name="package_token"]')?.value;
    const programsForcePackage = document.querySelector('input[name="force_package"]')?.value;
    const programsClassroomTypeIds = document.querySelector('input[name="classroom_type_ids"]')?.value;
    if (programsPackageToken) url += `&package_token=${encodeURIComponent(programsPackageToken)}`;
    if (programsForcePackage) url += `&force_package=${encodeURIComponent(programsForcePackage)}`;
    if (programsClassroomTypeIds) url += `&classroom_type_ids=${encodeURIComponent(programsClassroomTypeIds)}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);

        this.trackStep("classroom_office", { office_id: officeId });

        // Hide the office picker and show the program picker
        let usBookingClassroomProgramOfficePicker = document.getElementById(
          "us-booking-classroom-program-office-picker"
        );
        usBookingClassroomProgramOfficePicker.hidden = true;

        let usBookingFormClassroomProgramPicker = document.getElementById(
          "us-booking-form-classroom-program-picker"
        );
        usBookingFormClassroomProgramPicker.hidden = false;

        // Hide spinner and enable button on success
        if (spinner) {
          spinner.classList.add("u-hidden");
          spinner.classList.remove("u-inlineBlock");
        }
        button.disabled = false;

        // scroll to the top of the page
        window.scrollTo(0, 0);
      } else {
        // Hide spinner and enable button on error
        if (spinner) {
          spinner.classList.add("u-hidden");
          spinner.classList.remove("u-inlineBlock");
        }
        button.disabled = false;
        console.error("Error fetching classroom programs:", response.statusText);
      }
    } catch (error) {
      // Hide spinner and enable button on error
      if (spinner) {
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
      }
      button.disabled = false;
      console.error("Error fetching classroom programs:", error);
    }
  }

  payDepositPressed(event) {
    if (this.formSubmitted) {
      event.preventDefault();
      return;
    }

    // Get the deposit amount from the button's data attribute
    const depositAmount = event.currentTarget.dataset.depositAmount;

    // Error if deposit amount is not set
    if (!depositAmount || depositAmount === "" || parseFloat(depositAmount) <= 0) {
      event.preventDefault();
      alert("Error: Deposit amount is not configured. Please contact the school administrator.");
      return;
    }

    if (this.haspayDepositAndTrackTarget) {
      this.payDepositAndTrackTarget.value = "true";
    } else {
      const depositPackage = document.getElementById(
        "driving-school-deposit-package"
      );
      if (depositPackage) {
        depositPackage.value = "true";
      }
    }

    // Use the dynamic deposit amount
    this.depositAmountTarget.value = depositAmount;

    if (this.hasPayLaterAndTrackTarget) {
      this.payLaterAndTrackTarget.value = "false";
    }

    const button = event.currentTarget;

    // Show spinner and disable the deposit button
    const spinnerTarget = event.currentTarget.querySelector('[id$="-spinner"]');
    if (spinnerTarget) {
      spinnerTarget.classList.remove("u-hidden");
      spinnerTarget.classList.add("u-inlineBlock");
    }
    button.disabled = true;

    const form = button.closest("form");

    // Disable other buttons but keep them visible
    const originalPaymentButton = document.getElementById("payment-button");
    if (originalPaymentButton) {
      originalPaymentButton.disabled = true;
    }

    const payLaterButton = document.getElementById("pay-later-button");
    if (payLaterButton) {
      payLaterButton.disabled = true;
    }

    // Disable all other deposit buttons (if any)
    document.querySelectorAll('[id^="pay-deposit-button"]').forEach(btn => {
      if (btn !== button) {
        btn.disabled = true;
      }
    });

    if (form) {
      // This will result in a full page navigation that can properly follow redirects
      this.formSubmitted = true;
      this.element.setAttribute("data-remote", "false");
      this.element.submit();
    }
  }

  continueToPaymentPressed(event) {
    if (this.formSubmitted) {
      event.preventDefault();
      return;
    }

    const button = document.getElementById("payment-button");
    const form = button.closest("form");

    if (form) {
      this.formSubmitted = true;

      // Show spinner on the button
      const spinner = document.getElementById("payment-button-spinner");
      if (spinner) {
        spinner.classList.remove('u-hidden');
        spinner.classList.add('u-inlineBlock');
      }
      if (button) {
        button.disabled = true;
      }

      // Submit the form using traditional form submission (not AJAX)
      // This will result in a full page navigation that can properly follow redirects
      this.element.setAttribute("data-remote", "false");
      this.element.submit();
    }
  }

  payLaterPressed(event) {
    if (this.formSubmitted) {
      event.preventDefault();
      return;
    }

    // if (this.hasDepositAmountTarget) {
    //   this.depositAmountTarget.value = undefined;
    // }
    //
    // if (this.hasdepositAmountTarget) {
    //   this.depositAmountTarget.value = undefined;
    // }

    if (this.haspayDepositAndTrackTarget) {
      this.payDepositAndTrackTarget = "false";
    }

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.hidden = true;
    }

    this.payLaterAndTrackTarget.value = "true";

    const spinnerTarget = document.getElementById("pay-later-button-spinner");
    if (spinnerTarget) {
      // show the spinner
      spinnerTarget.classList.remove("u-hidden");
    }

    const button = document.getElementById("pay-later-button");
    button.disabled = true;
    // Get the form element
    const form = button.closest("form");

    const originalPaymentButton = document.getElementById("payment-button");
    if (originalPaymentButton) {
      originalPaymentButton.disabled = true;
      originalPaymentButton.hidden = true;
    }

    const payDepositButton = document.getElementById("pay-deposit-button");
    if (payDepositButton) {
      payDepositButton.disabled = true;
      payDepositButton.hidden = true;
    }

    // Submit the form programmatically
    if (form) {
      form.submit();
    }
  }

  async nextButtonClassroomProgramSelectedPressed(event) {
    event.preventDefault();

    const selectedProgramInput = document.querySelector('input[name="selected_program_id"]');
    const classroomProgramId = selectedProgramInput?.value || null;
    this.updateClassroomPackages(classroomProgramId);

    const hasActivePrograms = this.nextButtonClassroomProgramSelectedTarget.dataset.hasActivePrograms;
    if (hasActivePrograms === "true") {
      await this.updatePrograms(classroomProgramId);
    }

    this.trackStep("classroom_program", {
      classroom_program_id: classroomProgramId,
      attendance_type: this.getClassroomAttendanceType() || null,
    });

    await this.showNextStepAfterClassroomProgramSelected(classroomProgramId);
  }

  showClassroomStudentDetailsForm(previousStep) {
    const detailsForm = document.getElementById("us-booking-form-1");
    if (detailsForm) {
      detailsForm.dataset.previousStep = previousStep;
      detailsForm.hidden = false;
    }
  }

  getClassroomAttendanceType() {
    const el = document.querySelector(
      'input[name="selected_classroom_attendance_type"]'
    );
    return el && el.value ? el.value.trim() : "";
  }

  async showNextStepAfterClassroomProgramSelected(classroomProgramId) {
    const schoolToken = document.getElementById("us-school-token").innerHTML.trim();
    const zipcode = document.getElementById("zipcode_value")?.value || "";
    const dateOfBirth = this.getDateofBirth();

    let data = { is_remote: false, has_pickup_points: false };
    if (schoolToken && zipcode.length >= 5 && classroomProgramId) {
      const jsonParams = {
        school_token: schoolToken,
        zipcode: zipcode,
        classroom_program_id: classroomProgramId,
        locale: this.getLocale(),
      };
      if (dateOfBirth) {
        jsonParams.date_of_birth = dateOfBirth;
      }
      const attendanceType = this.getClassroomAttendanceType();
      if (attendanceType) {
        jsonParams.classroom_attendance_type = attendanceType;
      }

      const url =
        `/us/${schoolToken}/classroom/pick-up-locations.json?` +
        new URLSearchParams(jsonParams).toString();
      try {
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        if (response.ok) {
          data = await response.json();
        }
      } catch (e) {
        console.error("classroom pick up location fetch failed:", e);
      }
    }

    const enrollmentSection = document.getElementById(
      "driving_school_student_program_enrollment_section"
    );
    const hasStudentPrograms =
      enrollmentSection?.dataset?.hasStudentPrograms === "true";

    if (data.is_remote) {
      await this.fetchClassroomPickUpLocations(classroomProgramId);
      this.bookingFormClassroomProgramPickerTarget.hidden = true;
      const pickUpEl = document.getElementById("us-booking-form-classroom-pick-up-location");
      if (pickUpEl) {
        pickUpEl.dataset.previousStep = BOOKING_FLOW_STEP.CLASSROOM_PROGRAM_PICKER;
        pickUpEl.dataset.nextStep = hasStudentPrograms
          ? BOOKING_FLOW_STEP.AVAILABLE_STUDENT_PROGRAMS
          : BOOKING_FLOW_STEP.STUDENT_DETAILS;
        pickUpEl.hidden = false;
      }
      window.scrollTo(0, 0);
      return;
    }

    this.bookingFormClassroomProgramPickerTarget.hidden = true;

    if (hasStudentPrograms) {
      this.bookingFormAvailableStudentProgramsTarget.hidden = false;
      this.bookingFormAvailableStudentProgramsTarget.dataset.previousStep =
        BOOKING_FLOW_STEP.CLASSROOM_PROGRAM_PICKER;
    } else {
      this.showClassroomStudentDetailsForm(
        BOOKING_FLOW_STEP.CLASSROOM_PROGRAM_PICKER
      );
    }
    window.scrollTo(0, 0);
  }

  nextButtonAvailableStudentProgramsPressed(event) {
    event.preventDefault();

    const errors = this.validateStudentProgramEnrollmentForm();

    const validationMessage = document.getElementById("validation-message-student-program-enrollment");
    if (validationMessage) {
      if (errors.length > 0) {
        validationMessage.innerHTML = errors.join("<br>");
        validationMessage.hidden = false;
        return;
      } else {
        validationMessage.hidden = true;
        validationMessage.innerHTML = "";
      }
    }

    const selectedProgramInput = document.querySelector('input[name="selected_program_id"]');
    const classroomProgramId = selectedProgramInput?.value || null;
    const selectedStudentProgramRadio = document.querySelector(
      'input[name="student_program_enrollment[student_program_id]"]:checked'
    );
    const studentProgramId = selectedStudentProgramRadio?.value || null;
    this.updateClassroomPackages(classroomProgramId, studentProgramId);

    this.trackStep("classroom_student_program", {
      classroom_program_id: classroomProgramId,
      student_program_id: studentProgramId,
    });

    this.bookingFormAvailableStudentProgramsTarget.hidden = true;
    this.showClassroomStudentDetailsForm(
      BOOKING_FLOW_STEP.AVAILABLE_STUDENT_PROGRAMS
    );
    window.scrollTo(0, 0);
  }

  async next3Pressed(event) {
    event.preventDefault();

    const errors = this.validateForm3();

    const validationMessage = document.getElementById("validation-message-3");

    if (errors.length > 0) {
      validationMessage.innerHTML = errors.join("<br>");
      validationMessage.hidden = false;
      return;
    }

    validationMessage.hidden = true;

    let address_line_1 = document.getElementById("address_line_1_value").value;
    let zipcode = document.getElementById("zipcode_value").value;
    let address_line_2 = document.getElementById("address_line_2_id").value;
    let city = document.getElementById("city_id").value;
    let state = document.getElementById("state_id").value;
    let country = document.getElementById("country_id").value;

    console.log({
      address_line_1,
      zipcode,
      address_line_2,
      city,
      state,
      country,
    });

    document.getElementById(
      "booking_location_pickup_home_work_address_value"
    ).value = address_line_1;
    document.getElementById(
      "booking_location_pickup_home_work_zipcode_value"
    ).value = zipcode;
    document.getElementById(
      "booking_location_pickup_home_work_address_line_2_value"
    ).value = address_line_2;
    document.getElementById(
      "booking_location_pickup_home_work_city_value"
    ).value = city;
    document.getElementById(
      "booking_location_pickup_home_work_state_value"
    ).value = state;
    document.getElementById(
      "booking_location_pickup_home_work_country_value"
    ).value = country;

    document.getElementById(
      "booking_location_drop_off_home_work_address_value"
    ).value = address_line_1;
    document.getElementById(
      "booking_location_drop_off_home_work_zipcode_value"
    ).value = zipcode;
    document.getElementById(
      "booking_location_drop_off_home_work_address_line_2_value"
    ).value = address_line_2;
    document.getElementById(
      "booking_location_drop_off_home_work_city_value"
    ).value = city;
    document.getElementById(
      "booking_location_drop_off_home_work_state_value"
    ).value = state;
    document.getElementById(
      "booking_location_drop_off_home_work_country_value"
    ).value = country;

    const driving_school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();

    let extraParams = "";
    if (document.getElementById("pick_up_location_id")) {
      const pickupLocationId = document.getElementById(
        "pick_up_location_id"
      ).value;
      const pickUpCategory = document.getElementById("pick_up_category").value;
      const dropoffLocationId = document.getElementById(
        "drop_off_location_id"
      ).value;
      const dropOffCategory =
        document.getElementById("drop_off_category").value;
      extraParams = `&pick_up_location_id=${pickupLocationId}&pick_up_category=${pickUpCategory}&drop_off_location_id=${dropoffLocationId}&drop_off_category=${dropOffCategory}`;
    }

    const locale = this.getLocale();
    extraParams += `&city=${document.getElementById("city_id").value}&state=${
      document.getElementById("state_id").value
    }&country=${document.getElementById("country_id").value}&locale=${locale}`;

    // Replace the URL with the correct endpoint for fetching locations
    const url = this.get_fetch_locations_url(
      zipcode,
      address_line_1,
      driving_school_token,
      extraParams
    );
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const stream = await response.text();

      Turbo.renderStreamMessage(stream);
      if (this.shouldSkipBtwSlotSelectionOverride()) {
        this.bookingForm3Target.hidden = true;
        this.bookingForm1Target.hidden = false;
        delete this.bookingForm1Target.dataset.previousStep;
        window.scrollTo(0, 0);
        return;
      }

      this.trackStep("btw_location", {
        zipcode: zipcode,
        city: city,
        state: state,
      });

      this.showBookingForm4();

      this.checkValueInStream(stream).then((isTrue) => {
        let enrollmentTypeElement = document.getElementById(
          "us-school-enrollment-flow"
        );
        let enrollmentType = enrollmentTypeElement
          ? enrollmentTypeElement.innerHTML.trim()
          : "";

        if (isTrue === false || enrollmentType == "classroom") {
          this.clear_pick_up_school_selection_fields();
          this.clear_drop_off_school_selection_fields();
          this.reset_location_panel_selection("school-pick-up-points-");
          this.reset_location_panel_selection("school-drop-off-points-");
          // alert('xxxx')
          // make this booking_location_office disabled
          const officeLocation = document.getElementById(
            "booking_location_office"
          );
          officeLocation.disabled = true;
          // booking_location_pickup select this radio button
          const pickupLocation = document.getElementById(
            "booking_location_pickup"
          );
          pickupLocation.checked = true;

          // find booking-location-pickup-points and show it
          document.getElementById(
            "booking-location-pickup-points"
          ).hidden = false;
          // booking-location-pickup-home-work and hide it
          document.getElementById(
            "booking-location-pickup-home-work"
          ).hidden = true;
          document.getElementById(
            "booking-location-pickup-schools"
          ).hidden = true;

          // find booking_drop_off_office and hide it
          document.getElementById("booking_drop_off_office").disabled = true;

          // booking-location-drop-off-home-work and hide it
          document.getElementById(
            "booking-location-drop-off-home-work"
          ).hidden = true;

          // find booking-location-drop-off-points and show it
          document.getElementById(
            "booking-location-drop-off-points"
          ).hidden = false;
          document.getElementById(
            "booking-location-drop-off-schools"
          ).hidden = true;

          // booking-location-drop-off-home-work and select it
          const dropOffLocation = document.getElementById(
            "booking_drop_off_points"
          );
          dropOffLocation.checked = true;
        } else if (this.applyPreselectedPackagePickupRestriction()) {
          this.clear_drop_off_location_selection_fields();
          this.reset_location_panel_selection("drop-off-points-");
          this.reset_location_panel_selection("school-drop-off-points-");

          const dropOffOffice = document.getElementById("booking_drop_off_office");
          if (dropOffOffice) {
            dropOffOffice.disabled = false;
            dropOffOffice.checked = true;
          }

          const dropOffPoints = document.getElementById("booking_drop_off_points");
          if (dropOffPoints) {
            dropOffPoints.checked = false;
          }

          document.getElementById(
            "booking-location-drop-off-points"
          ).hidden = true;
          document.getElementById(
            "booking-location-drop-off-schools"
          ).hidden = true;
          document.getElementById(
            "booking-location-drop-off-home-work"
          ).hidden = false;
        } else {
          this.clear_pick_up_location_selection_fields();
          this.clear_drop_off_location_selection_fields();
          this.reset_location_panel_selection("pick-up-points-");
          this.reset_location_panel_selection("school-pick-up-points-");
          this.reset_location_panel_selection("drop-off-points-");
          this.reset_location_panel_selection("school-drop-off-points-");
          this.setPickupCategoryOptionVisibility("booking_location_office", true);
          this.setPickupCategoryOptionVisibility("booking_location_school", true);
          this.setPickupCategoryOptionVisibility("booking_location_pickup", true);
          // make booking_location_office enabled
          const officeLocation = document.getElementById(
            "booking_location_office"
          );
          officeLocation.disabled = false;
          officeLocation.checked = true;

          // booking-location-pickup-home-work and show it
          document.getElementById(
            "booking-location-pickup-home-work"
          ).hidden = false;

          // find booking-location-pickup-points and hide it
          document.getElementById(
            "booking-location-pickup-points"
          ).hidden = true;
          document.getElementById(
            "booking-location-pickup-schools"
          ).hidden = true;

          // booking_location_pickup uncheck this radio button
          const pickupLocation = document.getElementById(
            "booking_location_pickup"
          );
          pickupLocation.checked = false;

          // find booking_drop_off_office and enable it
          const dropOffOffice = document.getElementById("booking_drop_off_office");
          dropOffOffice.disabled = false;
          // booking_drop_off_office is selected
          dropOffOffice.checked = true;

          // find booking-location-drop-off-points and hide it
          document.getElementById(
            "booking-location-drop-off-points"
          ).hidden = true;
          document.getElementById(
            "booking-location-drop-off-schools"
          ).hidden = true;

          // booking-location-drop-off-home-work and show it
          document.getElementById(
            "booking-location-drop-off-home-work"
          ).hidden = false;

          // booking_drop_off_points uncheck this radio button
          const dropOffLocation = document.getElementById(
            "booking_drop_off_points"
          );
          dropOffLocation.checked = false;
        }
      });
    } else {
      const addressError = this.getAddressInvalidError();
      validationMessage.innerHTML = addressError;
      validationMessage.hidden = false;
    }
  }

  async checkValueInStream(streamText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(streamText, "text/html");
    const turboStream = doc.querySelector(
      'turbo-stream[target="locations-list-is-in-zone"]'
    );

    console.log("Turbo Stream found:", turboStream !== null);

    if (turboStream) {
      const template = turboStream.querySelector("template");

      if (template) {
        const templateContent = template.content;
        const div = templateContent.querySelector(
          'div[id="locations-list-is-address-in-zone-div"]'
        );

        console.log("Div found in template:", div !== null);

        if (div) {
          const textContent = div.textContent.trim();
          console.log("Text content:", textContent);
          return textContent === "true";
        }
      }
    }

    return false;
  }

  get_fetch_locations_url(
    zipcode,
    address_line_1,
    driving_school_token,
    extraParams
  ) {
    if (extraParams == null) {
      return `/fetch_locations?zipcode=${zipcode}&address=${address_line_1}&driving_school_token=${driving_school_token}`;
    } else {
      return `/fetch_locations?zipcode=${zipcode}&address=${address_line_1}&driving_school_token=${driving_school_token}&${extraParams}`;
    }
  }

  async dateOfBirthChanged(event) {
    alert("dateOfBirthChanged");
    const dateOfBirth = event.target.value;
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();

    const locationData = this.getLocationData();
    this.updatedPackagesOnZoneAndDateOfBirth(
      locationData.pick_up_address_line_1,
      locationData.pick_up_zipcode,
      locationData.pick_up_city,
      locationData.pick_up_state,
      locationData.pick_up_country,
      locationData.office_pickup_category,
      school_token,
      dateOfBirth
    );
  }

  checkAge() {
    this.relationShipToStudentChanged();
    const guardianSignature = document.getElementById("guardian-signature");
    if (guardianSignature == null) {
      return;
    }

    let age = this.calculateAge();

    if (age >= 18) {
      // find guardian-signature and hide it
      document.getElementById("guardian-signature").hidden = true;
    } else {
      document.getElementById("guardian-signature").hidden = false;
    }

    // find classroom-enrollment-packages-teens check it exists
    let classroomEnrollmentPackagesTeens = document.getElementById(
      "classroom-enrollment-packages-teens"
    );
    let classroomEnrollmentPackagesAdults = document.getElementById(
      "classroom-enrollment-packages-adults"
    );
    if (classroomEnrollmentPackagesTeens && classroomEnrollmentPackagesAdults) {
      if (age >= 18) {
        // remove the HTML from classroom-enrollment-packages-teens from the DOM element
        classroomEnrollmentPackagesTeens.hidden = true;
        classroomEnrollmentPackagesAdults.hidden = false;
        classroomEnrollmentPackagesTeens.innerHTML = "";
      } else {
        classroomEnrollmentPackagesTeens.hidden = false;
        classroomEnrollmentPackagesAdults.hidden = true;
        classroomEnrollmentPackagesAdults.innerHTML = "";
      }
    }

    this.updatePermitFieldsRequiredFromRule();
  }

  async updatedPackagesOnZoneAndDateOfBirth(
    address,
    zipcode,
    city,
    state,
    country,
    pickupCategory,
    school_token,
    date_of_birth
  ) {
    this.syncOverrideSkipBtwSlotSelectionFromUrlAndStorage();
    // loop through where id like package_XXX_field and find the ID of the checked one
    const packageFields = document.querySelectorAll('[id^="package_"]');
    let selectedPackageId = null;
    packageFields.forEach((packageField) => {
      if (packageField.checked) {
        // get the number inbetween package_ and _field
        selectedPackageId = packageField.id.match(/package_(\d+)_field/)[1];
      }
    });

    const locale = this.getLocale();
    let url = `/schools/us/dashboard/update_packages?date_of_birth=${date_of_birth}&address=${address}&zipcode=${zipcode}&school_token=${school_token}&is_admin=false&selected_package_id=${selectedPackageId}&city=${city}&state=${state}&country=${country}&pickup_category=${encodeURIComponent(pickupCategory || "")}&locale=${locale}`;
    const packageToken = document.querySelector('input[name="package_token"]')?.value;
    const forcePackage = document.querySelector('input[name="force_package"]')?.value;
    const overrideSkipBtwSlot = document.querySelector(
      'input[name="override_skip_btw_slot_selection"]'
    )?.value;

    if (packageToken) {
      url += `&package_token=${encodeURIComponent(packageToken)}`;
    }
    if (forcePackage) {
      url += `&force_package=${encodeURIComponent(forcePackage)}`;
    }
    if (overrideSkipBtwSlot === "true" || overrideSkipBtwSlot === "1") {
      url += "&override_skip_btw_slot_selection=true";
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const stream = await response.text();
      Turbo.renderStreamMessage(stream);
    }
  }

  async updatedPackagesForClassroom(
    zipcode,
    school_token,
    date_of_birth,
    classroomProgramId = null,
    studentProgramId = null,
    pickUpPointId = null
  ) {
    const packageFields = document.querySelectorAll('[id^="package_"]');

    let selectedPackageId = null;
    packageFields.forEach((packageField) => {
      if (packageField.checked) {
        selectedPackageId = packageField.id.match(/package_(\d+)_field/)[1];
      }
    });

    if (!studentProgramId) {
      const selectedStudentProgramInput = document.querySelector('input[name="student_program_enrollment[student_program_id]"]:checked');
      if (selectedStudentProgramInput && selectedStudentProgramInput.value) {
        studentProgramId = selectedStudentProgramInput.value;
      }
    }

    const locale = this.getLocale();
    let url = `/schools/us/dashboard/update_classroom_packages?date_of_birth=${date_of_birth}&zipcode=${zipcode}&school_token=${school_token}&is_admin=false&locale=${locale}`;

    if (selectedPackageId) {
      url += `&selected_package_id=${selectedPackageId}`;
    }
    const packageToken = document.querySelector('input[name="package_token"]')?.value;
    const forcePackage = document.querySelector('input[name="force_package"]')?.value;
    const classroomTypeIds = document.querySelector('input[name="classroom_type_ids"]')?.value;

    if (packageToken) {
      url += `&package_token=${encodeURIComponent(packageToken)}`;
    }
    if (forcePackage) {
      url += `&force_package=${encodeURIComponent(forcePackage)}`;
    }
    if (classroomTypeIds) {
      url += `&classroom_type_ids=${encodeURIComponent(classroomTypeIds)}`;
    }

    if (classroomProgramId) {
      url += `&classroom_program_id=${classroomProgramId}`;
    }
    if (studentProgramId) {
      url += `&student_program_id=${studentProgramId}`;
    }
    if (pickUpPointId) {
      url += `&pick_up_point_id=${encodeURIComponent(pickUpPointId)}`;
    }
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const stream = await response.text();
      Turbo.renderStreamMessage(stream);
    }
  }

  async next4Pressed(event) {
    event.preventDefault();

    const allowStudentsToScheduleElement = document.getElementById("driving-school-rule-allow-students-to-schedule");
    const allowStudentsToSchedule = allowStudentsToScheduleElement ? allowStudentsToScheduleElement.innerHTML.trim() === "true" : false;

    if (!allowStudentsToSchedule) {
      this.bookingForm4Target.hidden = true;
      this.bookingForm1Target.dataset.previousStep = BOOKING_FLOW_STEP.OFFICE;
      this.bookingForm1Target.hidden = false;
      window.scrollTo(0, 0);
      return;
    }

    let button = event.currentTarget;
    let spinner = button.querySelector(".Spinner");
    spinner.classList.remove("u-hidden");
    spinner.classList.add("u-inlineBlock");
    button.disabled = true;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const school_token = document.getElementById("us-school-token").innerHTML;

    const locationData = this.getLocationData();

    const url = "/us/slots/check-zones/";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        driving_school_token: school_token,
        pick_up_category: locationData.office_pickup_category,
        drop_off_category: locationData.office_drop_off_category,
        pick_up_address_line_1: locationData.pick_up_address_line_1,
        pick_up_zipcode: locationData.pick_up_zipcode,
        pick_up_school_id: locationData.pick_up_school_id,
        pick_up_point_id: locationData.pick_up_point_id,
        pick_up_city: locationData.pick_up_city,
        pick_up_state: locationData.pick_up_state,
        pick_up_country: locationData.pick_up_country,
        drop_off_address_line_1: locationData.drop_off_address_line_1,
        drop_off_zipcode: locationData.drop_off_zipcode,
        drop_off_school_id: locationData.drop_off_school_id,
        drop_off_point_id: locationData.drop_off_point_id,
        drop_off_city: locationData.drop_off_city,
        drop_off_state: locationData.drop_off_state,
        drop_off_country: locationData.drop_off_country,
      }),
    });

    if (response.ok) {
      let enrollmentTypeElement = document.getElementById(
        "us-school-enrollment-flow"
      );
      let enrollmentType = enrollmentTypeElement
        ? enrollmentTypeElement.innerHTML.trim()
        : "";
      if (enrollmentType == "classroom") {
        let todayDate = new Date();
        todayDate.setDate(todayDate.getDate());
        todayDate = this.formatDateToString(todayDate);

        this.bookingForm4Target.hidden = true;
        this.bookingFormClassroomProgramPickerTarget.hidden = false;

        let spinner = button.querySelector(".Spinner");
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
        button.disabled = false;
      } else {
        let todayDate = new Date();
        todayDate.setDate(todayDate.getDate());
        todayDate = this.formatDateToString(todayDate);

        if (allowStudentsToSchedule) {
          console.log("allow_students_to_schedule is true");
          this.bookingForm4Target.hidden = true;
          this.bookingForm5Target.hidden = false;
          this.set_data_attributes_on_weekview();
          this.get_next_slot_day(todayDate, "calendar_next_week", true);
        } else {
          this.bookingForm4Target.hidden = true;
          this.bookingForm1Target.dataset.previousStep = BOOKING_FLOW_STEP.OFFICE;
          this.bookingForm1Target.hidden = false;
        }

        // scroll to top
        window.scrollTo(0, 0);

        let spinner = button.querySelector(".Spinner");
        spinner.classList.add("u-hidden");
        spinner.classList.remove("u-inlineBlock");
        button.disabled = false;
      }
      this.refreshInstructorSelection();
    } else {
      let spinner = button.querySelector(".Spinner");
      spinner.classList.add("u-hidden");
      spinner.classList.remove("u-inlineBlock");
      button.disabled = false;
      if (response.status === 404) {
        const data = await response.json();
        // show id: validation-message
        const validationMessage = document.getElementById(
          "location-validation-message"
        );
        validationMessage.hidden = false;

        if (data.pick_up_zone_nil && data.drop_off_zone_nil) {
          document.getElementById("validation-location-type").innerHTML =
            "pick up and drop off";
        } else if (data.pick_up_zone_nil) {
          document.getElementById("validation-location-type").innerHTML =
            "pick up";
        } else if (data.drop_off_zone_nil) {
          document.getElementById("validation-location-type").innerHTML =
            "drop off";
        }
      }
      if (response.status === 403) {
        const validationMessage = document.getElementById(
          "location-different-zone-validation-message"
        );
        validationMessage.hidden = false;
      }
    }
  }
  // selectDay is true if its the inital load of the schedule.
  async get_next_slot_day(start_date, action, selectDay) {
    this.showLoader();
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const locationData = this.getLocationData();
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const isAutomaticSelected = optionalChaining(
      () =>
        document.getElementById(
          "driving_school_booking_gearbox_preference_automatic"
        ).checked || false
    );

    // check start_date and make sure it is in the correct format YYYY-MM-DD and it isn't null and it isn't NaN-NaN-NaN
    if (
      start_date == null ||
      start_date == undefined ||
      start_date == "NaN-NaN-NaN" ||
      start_date == "Invalid Date"
    ) {
      alert(
        "The date picked is not valid. Please try again, or contact us if the problem persists."
      );
      return;
    }

    let instructorId = "Any";
    if (this.hasSlotSelectionInstructorIdTarget) {
      instructorId = this.slotSelectionInstructorIdTarget.value;
    }

    const response = await fetch(
      `/us/select-next-slot-day/${school_token}?start_time=${start_date}&week_action=${action}&week_change=${selectDay}&is_automatic=${isAutomaticSelected}&instructor_id=${instructorId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          driving_school_token: school_token,
          pick_up_category: locationData.office_pickup_category,
          drop_off_category: locationData.office_drop_off_category,
          pick_up_address_line_1: locationData.pick_up_address_line_1,
          pick_up_zipcode: locationData.pick_up_zipcode,
          pick_up_school_id: locationData.pick_up_school_id,
          pick_up_point_id: locationData.pick_up_point_id,
          pick_up_city: locationData.pick_up_city,
          pick_up_state: locationData.pick_up_state,
          pick_up_country: locationData.pick_up_country,
          drop_off_address_line_1: locationData.drop_off_address_line_1,
          drop_off_zipcode: locationData.drop_off_zipcode,
          drop_off_school_id: locationData.drop_off_school_id,
          drop_off_point_id: locationData.drop_off_point_id,
          drop_off_city: locationData.drop_off_city,
          drop_off_state: locationData.drop_off_state,
          drop_off_country: locationData.drop_off_country,
          is_initial_load: selectDay,
        }),
      }
    );
    if (response.ok) {
      const data = await response.json();
      const start_date = data.selected_date;
      const selected_day = data.selected_day;
      const week_start_date = data.week_start_date;
      const disabled_days = data.disabled_days;
      const no_slots_available = data.no_slots_available;
      const time_zone = data.time_zone;

      let date_with_time_zone = start_date
        ? Luxon.DateTime.fromISO(start_date, { zone: time_zone }).startOf("day")
        : Luxon.DateTime.now({ zone: time_zone }).startOf("day");

      if (week_start_date !== null) {
        await this.fetchAndUpdateCalendar(action, week_start_date);
      }
      this.disable_week_days(disabled_days);
      if (start_date !== null && selected_day !== null) {
        this.get_slots(null, date_with_time_zone);
        this.select_slot_day(selected_day);
      }
      this.displayNoSlotAvailable(!no_slots_available);
    } else {
      console.error("Request failed:", response.status);
    }
  }

  displayNoSlotAvailable(future_potentials_slots) {
    const noSlot = document.getElementById("no_slot_available");
    const weekView = document.getElementById("week-view-slots");
    if (future_potentials_slots) {
      if (noSlot) noSlot.hidden = true;
      if (weekView) weekView.hidden = false;
    } else {
      if (noSlot) noSlot.hidden = false;
      if (weekView) weekView.hidden = true;

      const nextButton = document.getElementById("us-booking-next-5");
      if (nextButton) nextButton.disabled = true;
    }
  }

  async disable_previous_week_button(week_start_date) {
    // check if the week_start_date is the same as today or earlier then find the element with the id 'calendar_previous_week' and add class 'Button--disabled' to it else remove the class
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const weekStartDate = new Date(week_start_date);
    const selectedDayButton = document.getElementById(
      "slots-picker-previous-week-button"
    );

    if (weekStartDate <= todayDate && selectedDayButton) {
      selectedDayButton.classList.add("Button--disabled");
    } else {
      if (selectedDayButton) {
        selectedDayButton.classList.remove("Button--disabled");
      }
    }
  }

  async select_slot_day(selected_day) {
    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    const selectedDayButton = document.getElementById(`day-${selected_day}`);
    selectedDayButton.classList.add("isActive");

    // Set the value of the hidden field with the label of the button that was pressed
    document.getElementById("picked_slot_day").value =
      selectedDayButton.textContent.trim();
    document.getElementById("picked_slot_time").value = "";
  }

  async disable_week_days(disabled_days) {
    disabled_days.forEach((day) => {
      const dayButton = document.getElementById(`day-${day}`);
      if (dayButton) {
        dayButton.classList.add("Button--disabled");
      }
    });
  }

  filter_on_transmission_change() {
    let todayDate = new Date();
    todayDate.setDate(todayDate.getDate());
    todayDate = this.formatDateToString(todayDate);

    this.get_next_slot_day(todayDate, "calendar_next_week", true);
  }

  async next5Pressed(event) {
    this.filterPackagesUsingTransmission();

    event.preventDefault();
    const validationMessage = document.getElementById("validation-message-5");

    const errors = this.validateForm5();

    if (errors.length > 0) {
      validationMessage.innerHTML = errors.join("<br>");
      validationMessage.hidden = false;
    } else {
      validationMessage.hidden = true;

      this.bookingForm5Target.hidden = true;
      this.bookingForm1Target.dataset.previousStep = BOOKING_FLOW_STEP.SLOTS;
      this.bookingForm1Target.hidden = false;
    }
  }

  async updatePackages() {
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const dateOfBirth = this.getDateofBirth();
    console.log(dateOfBirth);

    const location_data = this.getLocationData();

    this.updatedPackagesOnZoneAndDateOfBirth(
      location_data.pick_up_address_line_1,
      location_data.pick_up_zipcode,
      location_data.pick_up_city,
      location_data.pick_up_state,
      location_data.pick_up_country,
      location_data.office_pickup_category,
      school_token,
      dateOfBirth
    );
  }

  async updateClassroomPackages(classroomProgramId = null, studentProgramId = null, pickUpPointId = null) {
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const dateOfBirth = this.getDateofBirth();
    const zipcode = document.getElementById("zipcode_value").value;

    if (!classroomProgramId) {
      const selectedProgramInput = document.querySelector('input[name="selected_program_id"]');
      if (selectedProgramInput && selectedProgramInput.value) {
        classroomProgramId = selectedProgramInput.value;
      }
    }

    this.updatedPackagesForClassroom(
      zipcode,
      school_token,
      dateOfBirth,
      classroomProgramId,
      studentProgramId,
      pickUpPointId
    );
  }

  async updatePrograms(classroomProgramId = null) {
    const schoolToken = document.getElementById("us-school-token").innerHTML.trim();
    if (!schoolToken) return;

    const dateOfBirth = this.getDateofBirth();
    if (!dateOfBirth) return;

    // If no classroomProgramId provided, try to get it from the form
    if (!classroomProgramId) {
      const selectedProgramInput = document.querySelector('input[name="selected_program_id"]');
      if (selectedProgramInput && selectedProgramInput.value) {
        classroomProgramId = selectedProgramInput.value;
      }
    }

    const locale = this.getLocale();
    let url = `/schools/us/dashboard/update_programs?date_of_birth=${dateOfBirth}&school_token=${schoolToken}&locale=${locale}`;
    if (classroomProgramId) {
      url += `&classroom_program_id=${classroomProgramId}`;
    }
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const stream = await response.text();
      Turbo.renderStreamMessage(stream);
    }
  }

  bookingFormClassroomOfficePickerBackButtonPressed(event) {
    this.bookingFormClassroomProgramOfficePickerTarget.hidden = true;
    this.bookingFormClassroomProgramZipcodeTarget.hidden = false;

    window.scrollTo(0, 0);
  }

  bookingFormClassroomProgramPickerBackButtonPressed(event) {
    this.bookingFormClassroomProgramOfficePickerTarget.hidden = false;
    this.bookingFormClassroomProgramPickerTarget.hidden = true;

    window.scrollTo(0, 0);
  }

  bookingFormAvailableStudentProgramsBackButtonPressed() {
    this.bookingFormAvailableStudentProgramsTarget.hidden = true;
    const backTarget = this.bookingFormAvailableStudentProgramsTarget.dataset
      .previousStep;
    delete this.bookingFormAvailableStudentProgramsTarget.dataset.previousStep;

    if (backTarget === BOOKING_FLOW_STEP.CLASSROOM_PICK_UP_LOCATION) {
      const pickUpEl = document.getElementById("us-booking-form-classroom-pick-up-location");
      if (pickUpEl) {
        pickUpEl.hidden = false;
        pickUpEl.dataset.nextStep = BOOKING_FLOW_STEP.AVAILABLE_STUDENT_PROGRAMS;
      }
    } else {
      this.bookingFormClassroomProgramPickerTarget.hidden = false;
    }

    window.scrollTo(0, 0);
  }

  async fetchClassroomPickUpLocations(classroomProgramId) {
    const schoolToken = document.getElementById("us-school-token").innerHTML.trim();
    if (!schoolToken) return;

    const zipcode = document.getElementById("zipcode_value")?.value;
    const dateOfBirth = this.getDateofBirth();
    if (!zipcode || zipcode.length < 5) return;

    let programId = classroomProgramId;
    if (!programId) {
      const selectedProgramInput = document.querySelector('input[name="selected_program_id"]');
      if (selectedProgramInput && selectedProgramInput.value) {
        programId = selectedProgramInput.value;
      } else {
        return;
      }
    }

    const turboParams = {
      school_token: schoolToken,
      zipcode: zipcode,
      classroom_program_id: programId,
      locale: this.getLocale(),
    };
    if (dateOfBirth) {
      turboParams.date_of_birth = dateOfBirth;
    }
    const attendanceType = this.getClassroomAttendanceType();
    if (attendanceType) {
      turboParams.classroom_attendance_type = attendanceType;
    }
    const url =
      `/us/${schoolToken}/classroom/pick-up-locations?` +
      new URLSearchParams(turboParams).toString();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      });
      if (response.ok) {
        const stream = await response.text();
        if (stream) {
          Turbo.renderStreamMessage(stream);
        }
      }
    } catch (error) {
      console.error("Error fetching classroom pickup locations:", error);
    }
  }

  nextButtonClassroomPickUpLocationPressed(event) {
    event.preventDefault();

    const pickupPointId = document.querySelector('input[name="driving_school_booking[pick_up_pick_up_point_id]"]')?.value;
    const schoolId = document.querySelector('input[name="driving_school_booking[pick_up_high_school_id]"]')?.value;
    const pickupCategory = document.querySelector('input[name="driving_school_booking[pick_up_category]"]:checked')?.value;

    const pickUpForm = this.bookingFormPickUpLocationTarget;

    if (pickupCategory === 'Pick-up points' && !pickupPointId) {
      const validationMessage = document.getElementById("validation-message-classroom-pick-up-location");
      if (validationMessage) {
        validationMessage.textContent = this.getValidationError(
          pickUpForm,
          "pick_up_point_required",
          "Please select a pick-up point"
        );
        validationMessage.hidden = false;
      }
      return;
    }

    if (pickupCategory === 'School' && !schoolId) {
      const validationMessage = document.getElementById("validation-message-classroom-pick-up-location");
      if (validationMessage) {
        validationMessage.textContent = this.getValidationError(
          pickUpForm,
          "school_required",
          "Please select a school"
        );
        validationMessage.hidden = false;
      }
      return;
    }

    const validationMessage = document.getElementById("validation-message-classroom-pick-up-location");
    if (validationMessage) {
      validationMessage.hidden = true;
    }

    const resolvedPickUpPointId = pickupCategory === 'School' ? schoolId : pickupPointId;
    this.trackStep("classroom_pickup_location", { pickup_category: pickupCategory || null });
    this.updateClassroomPackages(null, null, resolvedPickUpPointId);

    const usBookingFormPickUpLocation = document.getElementById(
      "us-booking-form-classroom-pick-up-location"
    );
    const nextAfterPickup = usBookingFormPickUpLocation?.dataset.nextStep;
    if (usBookingFormPickUpLocation) {
      usBookingFormPickUpLocation.hidden = true;
    }

    if (nextAfterPickup === BOOKING_FLOW_STEP.AVAILABLE_STUDENT_PROGRAMS) {
      this.bookingFormAvailableStudentProgramsTarget.hidden = false;
      this.bookingFormAvailableStudentProgramsTarget.dataset.previousStep =
        BOOKING_FLOW_STEP.CLASSROOM_PICK_UP_LOCATION;
    } else {
      this.showClassroomStudentDetailsForm(
        BOOKING_FLOW_STEP.CLASSROOM_PICK_UP_LOCATION
      );
    }

    window.scrollTo(0, 0);
  }

  bookingFormClassroomPickUpLocationBackButtonPressed(event) {
    event.preventDefault();

    const usBookingFormPickUpLocation = document.getElementById(
      "us-booking-form-classroom-pick-up-location"
    );
    if (!usBookingFormPickUpLocation) return;

    usBookingFormPickUpLocation.hidden = true;
    delete usBookingFormPickUpLocation.dataset.nextStep;
    this.bookingFormClassroomProgramPickerTarget.hidden = false;

    window.scrollTo(0, 0);
  }

  backPressed(event) {
    event.preventDefault();

    const previousStep = this.bookingForm1Target.dataset.previousStep;

    this.bookingForm1Target.hidden = true;

    if (previousStep === BOOKING_FLOW_STEP.CLASSROOM_PICK_UP_LOCATION) {
      const el = document.getElementById("us-booking-form-classroom-pick-up-location");
      if (el) el.hidden = false;
    } else if (previousStep === BOOKING_FLOW_STEP.CLASSROOM_PROGRAM_PICKER) {
      if (this.hasBookingFormClassroomProgramPickerTarget) {
        this.bookingFormClassroomProgramPickerTarget.hidden = false;
      }
    } else if (previousStep === BOOKING_FLOW_STEP.AVAILABLE_STUDENT_PROGRAMS) {
      if (this.hasBookingFormAvailableStudentProgramsTarget) {
        this.bookingFormAvailableStudentProgramsTarget.hidden = false;
      }
    } else if (previousStep === BOOKING_FLOW_STEP.OFFICE) {
      if (this.hasBookingForm4Target) {
        this.bookingForm4Target.hidden = false;
      }
    } else if (previousStep === BOOKING_FLOW_STEP.SLOTS) {
      if (this.hasBookingForm5Target) {
        this.bookingForm5Target.hidden = false;
      }
    } else {
      const enrollmentTypeElement = document.getElementById("us-school-enrollment-flow");
      const enrollmentType = enrollmentTypeElement
        ? enrollmentTypeElement.innerHTML.trim()
        : "";

      if (enrollmentType === "classroom") {
        if (this.hasBookingFormClassroomProgramZipcodeTarget) {
          this.bookingFormClassroomProgramZipcodeTarget.hidden = false;
        }
      } else if (enrollmentType === "behind-the-wheel") {
        if (this.hasBookingForm3Target) {
          this.bookingForm3Target.hidden = false;
        }
      }
    }

    window.scrollTo(0, 0);
  }

  back2Pressed(event) {
    event.preventDefault();
    this.bookingForm1Target.hidden = false;
    this.bookingForm2Target.hidden = true;

    window.scrollTo(0, 0);
  }

  back3Pressed(event) {
    event.preventDefault();
    this.bookingForm2Target.hidden = false;
    this.bookingForm3Target.hidden = true;
    
    window.scrollTo(0, 0);
  }

  back4Pressed(event) {
    event.preventDefault();

    let enrollmentTypeElement = document.getElementById("us-school-enrollment-flow");
    let enrollmentType = enrollmentTypeElement ? enrollmentTypeElement.innerHTML.trim() : "";
    if (enrollmentType == "classroom") {
      this.bookingForm1Target.hidden = false;
      this.bookingFormClassroomProgramPackagePickerTarget.hidden = true;
    } else {
      this.bookingForm3Target.hidden = false;
      this.bookingForm4Target.hidden = true;
    }

    window.scrollTo(0, 0);
  }

  back5Pressed(event) {
    event.preventDefault();
    this.bookingForm4Target.hidden = false;
    this.bookingForm5Target.hidden = true;

    window.scrollTo(0, 0);
  }

  back6Pressed(event) {
    event.preventDefault();
    this.bookingForm2Target.hidden = false;
    this.bookingForm6Target.hidden = true;

    window.scrollTo(0, 0);
  }

  isValidDate(dateString) {
    const parts = dateString.split("-"); // Assuming the format is YYYY-MM-DD
    if (parts.length !== 3) {
      return false; // Not a valid format
    }

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JavaScript Date
    const day = parseInt(parts[2], 10);

    const date = new Date(year, month, day);

    // Check if the date has rolled over to the next month or year, which means the day was invalid
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return false; // The date rolled over, so it's invalid
    }

    return true;
  }

  getSelectedDate(prefix) {
    var year = document.getElementById(prefix + "_1i").value;
    var month = document.getElementById(prefix + "_2i").value;
    var day = document.getElementById(prefix + "_3i").value;
    return year + "-" + month + "-" + day; // returns a string in "YYYY-MM-DD" format
  }

  getDateFromSelectedDate(prefix) {
    var year = parseInt(document.getElementById(prefix + "_1i").value, 10);
    var month = parseInt(document.getElementById(prefix + "_2i").value, 10) - 1; // months are 0-indexed in JavaScript Date
    var day = parseInt(document.getElementById(prefix + "_3i").value, 10);
    return new Date(year, month, day);
  }

  // enroller() {
  //   if (
  //     document.getElementById("driving_school_booking_enrolling_for_myself")
  //       ?.checked
  //   ) {
  //     return "student";
  //   } else {
  //     return (
  //       document.querySelector(
  //         "#driving_school_booking_relationship_to_student"
  //       )?.value || "unknown"
  //     );
  //   }
  // }

  async validateForm1() {
    let validationErrors = [];

    const bookingForm1 = this.bookingForm1Target;

    const isStudentEmailRequired = this.isStudentEmailRequired();
    const isYourEmailRequired = this.isYourEmailRequired();

    // Check the email
    const yourEmailField = document.getElementById("email_value");
    const yourCellField = document.getElementById("cell_number_value");
    console.log(`yourEmailField: ${yourEmailField}`);

    if (yourEmailField && yourCellField) {
      const emailValue = yourEmailField.value || "";
      const emailIsEmpty = emailValue.trim() === "";
      const emailIsValid =
        /^\w+([\.-]?\w+|\+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(emailValue);
      const cellValue = yourCellField.value || "";
      const cellIsEmpty = cellValue.trim() === "";
      const cellIsValid = this.isValidPhoneNumber(cellValue);

      console.log(
        "isYourEmailRequired function returns:",
        this.isYourEmailRequired()
      );
      console.log("isYourEmailRequired variable is:", isYourEmailRequired);
      console.log("emailIsEmpty:", emailIsEmpty);

      if (isYourEmailRequired && cellIsEmpty) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "cell_number_invalid",
          "Please enter a valid cell number."
        );
        validationErrors.push(errorMessage);
      }

      if (!cellIsEmpty && !cellIsValid) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "cell_number_invalid",
          "Please enter a valid cell number by removing any brackets, hyphens, fullstops or spaces."
        );
        validationErrors.push(errorMessage);
      }

      if (isYourEmailRequired && emailIsEmpty) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.email",
          "Email address cannot be empty."
        );
        validationErrors.push(errorMessage);
      }

      if (!emailIsEmpty && !emailIsValid) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "email_invalid",
          "Please enter a valid email address."
        );
        validationErrors.push(errorMessage);
      }

      if (!emailIsEmpty && emailIsValid) {
        const emailTaken = await this.isEmailAlreadyTaken(emailValue);
        const guardianEmailTaken =
          await this.isGuardianSigningUpSelfAndHasEmailTaken(emailValue);

        if (
          guardianEmailTaken &&
          guardianEmailTaken.email_taken &&
          guardianEmailTaken.is_guardian
        ) {
          const guardianPassword = `${this.guardianPasswordTarget.value}` || "";
          const guardianPasswordCorrect = await this.isGuardianPasswordCorrect(
            emailValue,
            guardianPassword
          );
          if (!guardianPasswordCorrect) {
            const errorMessage = this.getValidationError(
              bookingForm1,
              "guardian_email_invalid_password",
              "The password for this parent/guardian email is invalid. Please check your email and password then try again."
            );
            validationErrors.push(errorMessage);
          }
        } else if (emailTaken) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "email_taken",
            "This email is already taken."
          );
          validationErrors.push(errorMessage);
        }
      }
    }

    const applySchedulingPermitRule =
      bookingForm1.dataset.applySchedulingPermitRule === "true";
    const permitRequired = applySchedulingPermitRule
      ? this.permitRequiredForCurrentStudent(bookingForm1)
      : true;
    const validPermitDatesRequired = applySchedulingPermitRule
      ? permitRequired && this.validPermitDatesRequiredForCurrentStudent(bookingForm1)
      : true;

    const requiredFields = [
      {
        id: "first_name_value",
        name: "First name",
        key: "required.first_name",
      },
      { id: "last_name_value", name: "Last name", key: "required.last_name" },
      { id: "driving_school_booking_gender_female", name: "Gender" },
      { id: "how_did_you_hear_about_us", name: "How did you hear about us" },
      ...(permitRequired
        ? [
            {
              id: "permit_number_value",
              name: "Permit number",
              key: "required.permit",
            },
          ]
        : []),
    ];

    const guardianDetailsShown =
      document.querySelector("#booking-guardian-details").hidden === false;

    const bookingForOther = document.getElementById(
      "driving_school_booking_enrolling_for_someone_else"
    );
    const studentEmail = document.getElementById("student_email_value");
    const studentPhone = document.getElementById("student_cell_number_value");
    const yourFirstName = document.getElementById("your_first_name_value");
    const yourLastName = document.getElementById("your_last_name_value");
    const guardianEmail = document.getElementById("guardian_email_value");
    const guardianPhone = document.getElementById("guardian_cell_number_value");
    const guardianFirstName = document.getElementById(
      "guardian_first_name_value"
    );
    const guardianLastName = document.getElementById(
      "guardian_last_name_value"
    );

    if (bookingForOther.checked) {
      if (studentEmail.value.trim() === "" && isStudentEmailRequired) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.student_email",
          "Student email cannot be empty."
        );
        validationErrors.push(errorMessage);
      }
      if (studentPhone.value.trim() === "" && isStudentEmailRequired) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.student_phone",
          "Student cell number cannot be empty."
        );
        validationErrors.push(errorMessage);
      }
      if (yourFirstName.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.your_first_name",
          "Your first name cannot be empty."
        );
        validationErrors.push(errorMessage);
      }
      if (yourLastName.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.your_last_name",
          "Your last name cannot be empty."
        );
        validationErrors.push(errorMessage);
      }
    } else {
      if (studentEmail.value.trim() === "" && isStudentEmailRequired) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.student_email",
          "Student email cannot be empty."
        );
        validationErrors.push(errorMessage);
      }
    }

    // check student-signature is not empty
    if (this.studentSignaturePad) {
      let studentSignature = this.element.querySelector("#student-signature");
      if (
        this.studentSignaturePad.isEmpty() &&
        studentSignature.classList.contains("u-hidden") == false
      ) {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "signature_empty",
          "Please ensure the student signature is not empty."
        );
        validationErrors.push(errorMessage);
      }
    }

    // check student_email_value and if it is not null or empty, then check if it is a valid email address
    if (studentEmail) {
      if (studentEmail.value.trim() !== "") {
        if (
          !/^\w+([\.-]?\w+|\+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
            studentEmail.value
          )
        ) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "student_email_invalid",
            "Please enter a valid email address for student."
          );
          validationErrors.push(errorMessage);
        } else if (isStudentEmailRequired) {
          const encodedEmail = btoa(studentEmail.value);
          try {
            const response = await fetch(`/us/student/${encodedEmail}/taken`);
            const data = await response.json();
            if (data.email_taken === true) {
              const errorMessage = this.getValidationError(
                bookingForm1,
                "student_email_taken",
                "This student email is already taken."
              );
              validationErrors.push(errorMessage);
            }
          } catch (error) {
            console.error("Error:", error);
          }
          // Check student email is not same as other email
          if (
            studentEmail.value ===
              document.getElementById("email_value").value &&
            isStudentEmailRequired
          ) {
            const errorMessage = this.getValidationError(
              bookingForm1,
              "student_email_same",
              "Student email cannot be the same as your email."
            );
            validationErrors.push(errorMessage);
          }
        }
      }
    }

    const age = this.calculateAge();
    const isMinor = age < 18;
    if (!bookingForOther.checked && isMinor) {
      // The guardian details are required
      if (guardianEmail.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.guardian_email",
          "Parent/guardian email cannot be empty."
        );
        validationErrors.push(errorMessage);
      }

      if (guardianFirstName.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.guardian_first_name",
          "Parent/guardian first name cannot be empty."
        );
        validationErrors.push(errorMessage);
      }

      if (guardianLastName.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.guardian_last_name",
          "Parent/guardian last name cannot be empty."
        );
        validationErrors.push(errorMessage);
      }

      if (guardianPhone.value.trim() === "") {
        const errorMessage = this.getValidationError(
          bookingForm1,
          "required.guardian_phone",
          "Parent/guardian cell number cannot be empty."
        );
        validationErrors.push(errorMessage);
      }

    }

    if (guardianDetailsShown && guardianEmail) {
      if (guardianEmail.value.trim() !== "") {
        if (
          !/^\w+([\.-]?\w+|\+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
            guardianEmail.value
          )
        ) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "guardian_email_invalid",
            "Please enter a valid email address for guardian."
          );
          validationErrors.push(errorMessage);
        } else {
          const encodedEmail = btoa(guardianEmail.value);
          try {
            const response = await fetch(`/us/student/${encodedEmail}/taken`);
            const data = await response.json();
            if (data.email_taken === true) {
              const errorMessage = this.getValidationError(
                bookingForm1,
                "guardian_email_taken",
                "This parent/guardian email is already taken."
              );
              validationErrors.push(errorMessage);
              // validationErrors.push(
              //   "This parent/guardian email is already taken."
              // );
            }
          } catch (error) {
            console.error("Error:", error);
          }

          // Check guardian email is not same as other email
          if (
            guardianEmail.value === document.getElementById("email_value").value
          ) {
            const errorMessage = this.getValidationError(
              bookingForm1,
              "student_guardian_email_same",
              "Student and parent/guardian email cannot be the same"
            );
            validationErrors.push(errorMessage);
          }
        }
      }
    }

    for (let field_data of requiredFields) {
      let field = document.getElementById(field_data.id);

      if (field) {
        if (field.value.trim() === "") {
          const errorMessage = this.getValidationError(
            bookingForm1,
            field_data.key,
            `${field_data.name} cannot be empty.`
          );
          validationErrors.push(errorMessage);
        }

        if (
          [
            "driving_school_booking_gender_female",
            "driving_school_booking_gender_male",
            "driving_school_booking_gender_non-binary",
          ].includes(field_data.id)
        ) {
          const gender_female = document.getElementById(
            "driving_school_booking_gender_female"
          );
          const gender_male = document.getElementById(
            "driving_school_booking_gender_male"
          );
          const gender_non_binary = document.getElementById(
            "driving_school_booking_gender_non-binary"
          );

          const genderErrorMessage = this.getValidationError(
            bookingForm1,
            "gender_select",
            "Please select your gender."
          );

          if (gender_non_binary) {
            if (
              !gender_female.checked &&
              !gender_male.checked &&
              !gender_non_binary.checked
            ) {
              validationErrors.push(genderErrorMessage);
            }
          } else {
            if (!gender_female.checked && !gender_male.checked) {
              validationErrors.push(genderErrorMessage);
            }
          }
        }
      }
    }

    // if permit_number_value is visible on the page
    const permitNumber = document.getElementById("permit_number_value");
    if (permitNumber) {
      const shouldValidateFormat =
        permitRequired && bookingForm1.dataset.validatePermitFormat === "true";
      const permitNumberValue = permitNumber.value.trim();

      if (shouldValidateFormat && permitNumberValue !== "") {
        const schoolTokenElement = document.getElementById("us-school-token");
        const schoolToken = schoolTokenElement ? schoolTokenElement.innerHTML.trim() : null;
        const state = TOKEN_TO_STATE[schoolToken];
        let isValid = true;

        if (state) {
          const isPermitValid = isPermitValidForState(state, permitNumberValue);
          
          if (isPermitValid !== null) {
            isValid = isPermitValid;
          }
        } 

        if (!isValid) {
          const errorMessage = this.getValidationError(
              bookingForm1,
              "permit_format",
              "Permit number is in the incorrect format."
          );
          validationErrors.push(errorMessage);
        }
      }

      const issueDateString = this.getSelectedDate(
        "driving_school_booking_permit_issue_date"
      );
      const expiryDateString = this.getSelectedDate(
        "driving_school_booking_permit_expiration"
      );

      if (validPermitDatesRequired) {
        if (!this.isValidDate(issueDateString)) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "permit_issue_date_invalid",
            "Please enter a valid permit issue date."
          );
          validationErrors.push(errorMessage);
        }

        if (!this.isValidDate(expiryDateString)) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "permit_expiry_date_invalid",
            "Please enter a valid permit expiry date."
          );
          validationErrors.push(errorMessage);
        }

        // this returns a DATE object. If you need a string use getSelectedDate
        let issueDate = this.getDateFromSelectedDate(
          "driving_school_booking_permit_issue_date"
        );
        let expiryDate = this.getDateFromSelectedDate(
          "driving_school_booking_permit_expiration"
        ); // Adjust the prefix based on your expiry date fields' IDs

        if (issueDate > expiryDate) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "permit_expiry_before_issue",
            "The permit expiry date cannot be before the issue date."
          );
          validationErrors.push(errorMessage);
        }

        if (issueDateString === expiryDateString) {
          const errorMessage = this.getValidationError(
            bookingForm1,
            "permit_expiry_same_as_issue",
            "The permit expiry date cannot be the same as the issue date."
          );
          validationErrors.push(errorMessage);
        }
      }
    }

    let dateOfBirth = this.getSelectedDate(
      "driving_school_booking_date_of_birth"
    );

    if (!this.isValidDate(dateOfBirth)) {
      const errorMessage = this.getValidationError(
        bookingForm1,
        "date_of_birth_invalid",
        "Please enter a valid date of birth."
      );
      validationErrors.push(errorMessage);
    }

    return validationErrors;
  }

  isValidPhoneNumber(phoneNumberString) {
    const regex = /^\+?(\d{1,3}-?){0,2}\d{1,3}-?\d{1,4}$/;
    const hyphenCount = (phoneNumberString.match(/-/g) || []).length;
    const digitCount = phoneNumberString.replace(/\D/g, "").length;
    return (
      regex.test(phoneNumberString) &&
      hyphenCount <= 2 &&
      digitCount >= 10 &&
      digitCount <= 13
    );
  }

  goToSignIn() {
    window.location.href = "/users/sign_in";
  }

  dismissDuplicateEmailAlert() {
    if (!this.hasDuplicateEmailAlertTarget) return;
    this.duplicateEmailAlertTarget.hidden = true;
    this.guardianEmailAlreadyTakenAlertTarget.hidden = true;
  }

  /**
   * This function is called to refresh the instructor selection dropdown
   */
  async refreshInstructorSelection() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const school_token = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const locationData = this.getLocationData();

    let idOfSlotSelectionInstructorIdTarget = "";
    if (this.hasSlotSelectionInstructorIdTarget) {
      idOfSlotSelectionInstructorIdTarget =
        this.slotSelectionInstructorIdTarget.id;
    }

    const response = await fetch(
      `/us/slots/instructor-list-for-student-booking`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          driving_school_token: school_token,
          pick_up_category: locationData.office_pickup_category,
          drop_off_category: locationData.office_drop_off_category,
          pick_up_address_line_1: locationData.pick_up_address_line_1,
          pick_up_zipcode: locationData.pick_up_zipcode,
          pick_up_school_id: locationData.pick_up_school_id,
          pick_up_point_id: locationData.pick_up_point_id,
          pick_up_city: locationData.pick_up_city,
          pick_up_state: locationData.pick_up_state,
          pick_up_country: locationData.pick_up_country,
          drop_off_address_line_1: locationData.drop_off_address_line_1,
          drop_off_zipcode: locationData.drop_off_zipcode,
          drop_off_school_id: locationData.drop_off_school_id,
          drop_off_point_id: locationData.drop_off_point_id,
          drop_off_city: locationData.drop_off_city,
          drop_off_state: locationData.drop_off_state,
          drop_off_country: locationData.drop_off_country,
          item_selected_function:
            "driving-school-booking-form#changeInstructor",
          input_query_selector: `#${idOfSlotSelectionInstructorIdTarget}`,
        }),
      }
    );

    if (!response.ok) {
      alert("Error refreshing instructor selection");
      return;
    }

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById(
        "driving_school_admin_booking_form_v2_instructor_selector"
      );

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    } else {
      alert("Something went wrong");
    }
  }

  guardianEmailChangedIdForThrottling = null;
  async guardianEmailChanged(event) {
    // Function reserved for future use
  }

  emailChangedIdForThrottling = null;
  async emailChanged(event) {
    this.dismissDuplicateEmailAlert();
    if (event) event.preventDefault();

    const emailValue = document
      .getElementById("email_value")
      .value.toLowerCase();

    const randomId = Math.random().toString(36).substring(7);
    this.emailChangedIdForThrottling = randomId;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    // If the id has changed, then another request has been made and this one is no longer valid
    if (this.emailChangedIdForThrottling !== randomId) return;

    const isStudentSigningUpSelfAndHasEmailTaken =
      await this.isStudentSigningUpSelfAndHasEmailTaken(emailValue);
    if (isStudentSigningUpSelfAndHasEmailTaken === true) {
      this.duplicateEmailAlertTarget.hidden = false;
      return;
    }

    const isGuardianSigningUpSelfAndHasEmailTaken =
      await this.isGuardianSigningUpSelfAndHasEmailTaken(emailValue);

    if (
      isGuardianSigningUpSelfAndHasEmailTaken &&
      isGuardianSigningUpSelfAndHasEmailTaken.email_taken &&
      isGuardianSigningUpSelfAndHasEmailTaken.is_guardian
    ) {
      this.guardianEmailAlreadyTakenAlertTarget.hidden = false;
      return;
    }

    if (
      isGuardianSigningUpSelfAndHasEmailTaken &&
      isGuardianSigningUpSelfAndHasEmailTaken.email_taken &&
      !isGuardianSigningUpSelfAndHasEmailTaken.is_guardian
    ) {
      this.duplicateEmailAlertTarget.hidden = false;
      return;
    }
  }

  async isGuardianSigningUpSelfAndHasEmailTaken(email) {
    const isSelfSignUp = document.getElementById(
      "driving_school_booking_enrolling_for_myself"
    ).checked;
    if (isSelfSignUp) return false;
    const isEmailAlreadyTaken = await this.isGuardianEmailAlreadyTaken(email);
    return isEmailAlreadyTaken;
  }

  async isGuardianPasswordCorrect(email, password) {
    const encodedEmail = btoa(email);
    try {
      const response = await fetch(
        `/us/student/${encodedEmail}/guardian_login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            password: password,
          }),
        }
      );
      const data = await response.json();
      return !!data.success;
    } catch (error) {
      return false;
    }
  }

  async isStudentSigningUpSelfAndIsUnderageAndHasGuardianEmailTaken(email) {
    const isSelfSignUp = document.getElementById(
      "driving_school_booking_enrolling_for_myself"
    ).checked;
    if (!isSelfSignUp) return false;
    const age = this.calculateAge();
    const isUnderage = age < 18;
    if (!isUnderage) return false;

    const isEmailAlreadyTaken = await this.isGuardianEmailAlreadyTaken(email);
    return isEmailAlreadyTaken;
  }

  async isStudentSigningUpSelfAndHasEmailTaken(email) {
    const isSelfSignUp = document.getElementById(
      "driving_school_booking_enrolling_for_myself"
    ).checked;
    if (!isSelfSignUp) return false;
    const isEmailAlreadyTaken = await this.isEmailAlreadyTaken(email);
    return isEmailAlreadyTaken;
  }

  async isGuardianEmailAlreadyTaken(email) {
    const encodedEmail = btoa(email);
    try {
      const response = await fetch(
        `/us/student/${encodedEmail}/guardian_taken`
      );
      const data = await response.json();
      return {
        email_taken: data.email_taken,
        is_guardian: data.is_guardian,
      };
    } catch (error) {
      console.error("Error:", error);
      return {
        email_taken: undefined,
        is_guardian: undefined,
      };
    }
  }

  async isEmailAlreadyTaken(email) {
    if (!email) return undefined;
    const encodedEmail = btoa(email);
    try {
      const response = await fetch(`/us/student/${encodedEmail}/taken`);
      const data = await response.json();
      return !!data.email_taken;
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    }
  }

  emailCheckIdForThrottling = null;
  async isEmailAlreadyTakenWithThrottle(email) {
    const randomId = Math.random().toString(36).substring(7);
    this.emailCheckIdForThrottling = randomId;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    // If the id has changed, then another request has been made and this one is no longer valid
    // We return undefined to indicate that the result is ambiguous
    if (this.emailCheckIdForThrottling !== randomId) return undefined;
    const encodedEmail = btoa(email);
    try {
      const response = await fetch(`/us/student/${encodedEmail}/taken`);
      const data = await response.json();
      return !!data.email_taken;
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    }
  }

  validateStudentProgramEnrollmentForm() {
    const validationErrors = [];

    const programSection = document.getElementById("driving_school_student_program_enrollment_section");
    if (!programSection || programSection.hidden) return validationErrors;

    const selectedProgram = programSection.querySelector(
      'input[name="student_program_enrollment[student_program_id]"]:checked'
    );
    if (!selectedProgram) return validationErrors;

    const programId = selectedProgram.value || null;

    if (!programId) return validationErrors;

    const programPanel = selectedProgram.closest(
      '[data-driving-school-student-program-enrollment-form-target="programPanel"]'
    );
    if (!programPanel) return validationErrors;

    const financialScholarshipCheckbox = document.getElementById(
      `student_program_enrollment_has_financial_scholarship_${programId}`
    );

    const codeField = document.getElementById(`student_program_enrollment_code_${programId}`);

    if (!financialScholarshipCheckbox || !codeField) return validationErrors;

    if (!financialScholarshipCheckbox.checked) return validationErrors;

    const code = codeField.value.trim();
    const requiredLength = parseInt(codeField.dataset.codeLength || "0");

    if (code === "") {
      return validationErrors;
    }

    if (requiredLength > 0 && code.length !== requiredLength) {
      validationErrors.push(`Your code must be ${requiredLength} characters long.`);
    }

    return validationErrors;
  }

  validateForm3() {
    let validationErrors = [];

    const requiredFields = [
      {
        id: "address_line_1_value",
        name: "Address line 1",
        key: "required.address",
      },
      { id: "zipcode_value", name: "Zipcode", key: "required.zipcode" },
      { id: "city_id", name: "City", key: "required.city" },
      { id: "state_id", name: "State", key: "required.state" },
    ];

    let bookingForm3 = null;
    if (this.hasBookingForm3Target) {
       bookingForm3 = this.bookingForm3Target;
    } else {
       bookingForm3 = this.bookingFormClassroomProgramZipcodeTarget
    }

    requiredFields.forEach((field_data) => {
      let field = document.getElementById(field_data.id);

      if (field) {
        if (field.value.trim() === "") {
          const errorMessage = this.getValidationError(
            bookingForm3,
            field_data.key,
            `${field_data.name} cannot be empty.`
          );
          validationErrors.push(errorMessage);
        }
        if (
          field_data.id === "zipcode_value" &&
          field.value.trim().length != 5
        ) {
          const errorMessage = this.getValidationError(
            bookingForm3,
            "zipcode_length",
            `${field_data.name} must be 5 numbers.`
          );
          validationErrors.push(errorMessage);
        }
      }
    });

    return validationErrors;
  }

  validateForm5() {
    let validationErrors = [];

    const requiredFields = [{ id: "picked_slot_time", name: "Lesson slot" }];

    requiredFields.forEach((field_data) => {
      let field = document.getElementById(field_data.id);

      if (field) {
        if (field.value.trim() === "") {
          const next5Button = this.nextButton5Target;
          const slotError =
            next5Button?.dataset?.drivingSchoolBookingFormSlotError ||
            "You must pick a lesson slot";
          validationErrors.push(slotError);
        }
      }
    });

    return validationErrors;
  }

  validateForm6() {
    let validationErrors = [];

    const requiredFields = [{ id: "picked_slot_time", name: "Lesson slot" }];

    requiredFields.forEach((field_data) => {
      let field = document.getElementById(field_data.id);

      if (field) {
        if (field.value.trim() === "") {
          validationErrors.push(`You must pick a lesson slot`);
        }
      }
    });

    return validationErrors;
  }

  validateForm6() {
    let validationErrors = [];

    const radioButtons = this.radioTargets;
    let isChecked = false;

    for (let i = 0; i < radioButtons.length; i++) {
      if (radioButtons[i].checked) {
        isChecked = true;
        break;
      }
    }

    if (!isChecked) {
      validationErrors.push(`You must pick a package`);
    }

    return validationErrors;
  }

  filterPackagesUsingTransmission() {
    // const isAutomaticSelected = this.automaticRadioTarget.checked;
    // const isManualSelected = this.manualRadioTarget.checked;

    const isAutomaticSelected = optionalChaining(
      () =>
        document.getElementById(
          "driving_school_booking_gearbox_preference_automatic"
        ).checked || false
    );
    const isManualSelected = optionalChaining(
      () =>
        document.getElementById(
          "driving_school_booking_gearbox_preference_manual"
        ).checked || false
    );

    document
      .querySelectorAll("[data-driving-school-booking-form-is-automatic]")
      .forEach((item) => {
        item.hidden = true;
      });

    document
      .querySelectorAll("[data-driving-school-booking-form-is-manual]")
      .forEach((item) => {
        item.hidden = true;
      });

    if (isAutomaticSelected) {
      document
        .querySelectorAll(
          "[data-driving-school-booking-form-is-automatic='yes']"
        )
        .forEach((item) => {
          item.hidden = false;
        });
    }

    if (isManualSelected) {
      document
        .querySelectorAll("[data-driving-school-booking-form-is-manual='yes']")
        .forEach((item) => {
          item.hidden = false;
        });
    }
  }

  beginListeningForLocationChanges() {
    if (this.hasBookingForm4Target) {
      let el = this.bookingForm4Target;
      this.listenForValueAndDomChangesOnAndWithinElement(el, () => {
        this.set_data_attributes_on_weekview();
      });
    }
  }

  listenForValueAndDomChangesOnAndWithinElement(element, onChangeFunction) {
    if (!element) {
      return;
    }

    // create an observer instance
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        onChangeFunction();
      });
    });
    // Configure the MutationObserver to listen for attribute changes
    const observerConfiguration = {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    };
    // Observe all mutations within the element
    mutationObserver.observe(element, observerConfiguration);

    // Get all elements within the target element
    const allElementsWithinTarget = element.getElementsByTagName("*");
    // To all elements within the target element, add an event listener
    for (let i = 0; i < allElementsWithinTarget.length; i++) {
      const elementWithinTarget = allElementsWithinTarget[i];
      // Add an event listener to the element
      elementWithinTarget.addEventListener("input", (event) => {
        if (event.target instanceof HTMLInputElement) {
          onChangeFunction();
        }
      });
    }
  }

  getLocale() {
    if (this.hasLocaleTarget) {
      return this.localeTarget.value;
    }

    const localeElement = document.getElementById("us-school-locale");
    if (localeElement && localeElement.value) {
      return localeElement.value;
    }

    return "en";
  }

  getAddressInvalidError() {
    const next3Button = this.nextButton3Target;
    return (
      next3Button?.dataset?.drivingSchoolBookingFormAddressError ||
      "The address you entered is not valid or is not covered by this school. Please try again."
    );
  }

  getValidationError(form, key, fallbackMessage) {
    const localizedErrors = JSON.parse(
      form?.dataset?.drivingSchoolBookingFormErrors || "{}"
    );

    if (key.includes(".")) {
      const [parentKey, subKey] = key.split(".");
      return localizedErrors?.[parentKey]?.[subKey] || fallbackMessage;
    }

    return localizedErrors?.[key] || fallbackMessage;
  }

  async validateDiscountCode(event, skipPackageCheck = false) {
    const code = this.discountCodeInputTarget.value.trim();

    if (!code) {
      this.hideDiscountCodeMessage();
      return;
    }

    // Check if a package is selected (check both regular and classroom forms)
    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    const selectedPackageId = document.querySelector('input[name="selected_package_id"]')?.value;
    const hasPackageSelected = selectedPackage || (selectedPackageId && selectedPackageId !== '');

    if (!hasPackageSelected) {
      // Only show error if this was triggered by the Apply button click (not package change)
      if (!skipPackageCheck && event && event.type === 'click') {
        this.discountCodeMessageTarget.innerHTML = '<span class="u-textRed">Please select a package before applying discount code</span>';
        this.discountCodeMessageTarget.style.display = 'block';
        this.discountCodeMessageTarget.className = 'mt1';
      } else {
        // If package changed and no package selected, just hide any existing message
        this.hideDiscountCodeMessage();
      }
      return;
    }

    // Show spinner, hide message
    this.discountCodeSpinnerTarget.style.display = 'block';
    this.discountCodeMessageTarget.style.display = 'none';
    this.discountCodeButtonTarget.disabled = true;

    try {
      const schoolToken = document.querySelector('[name="driving_school_token"]')?.value;
      const selectedPackageValue = selectedPackage?.value || selectedPackageId;
      const url = new URL('/us/discount-code/validate', window.location.origin);
      url.searchParams.append('code', code);
      url.searchParams.append('school_token', schoolToken);
      if (selectedPackageValue) {
        url.searchParams.append('package_id', selectedPackageValue);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const data = await response.json();

      // Hide spinner
      this.discountCodeSpinnerTarget.style.display = 'none';
      // Re-enable button only if package is selected
      this.updateDiscountCodeButtonState();

      if (response.ok && data.valid) {
        // Show success message with discount details
        const discountText = data.formatted_discount || `${data.discount_type === 'Percent' ? data.discount_amount + '%' : '$' + data.discount_amount} off`;
        this.discountCodeMessageTarget.innerHTML = `<span class="u-textSuccessMid">✓ Discount applied: ${discountText}</span>`;
        this.discountCodeMessageTarget.style.display = 'block';
        this.discountCodeMessageTarget.className = 'mt1';

        // Store the discount code ID
        if (data.discount_code_id) {
          this.discountCodeIdTarget.value = data.discount_code_id;
        }
      } else {
        // Show error message
        this.discountCodeMessageTarget.innerHTML = `<span class="u-textRed">${data.error || 'Invalid discount code'}</span>`;
        this.discountCodeMessageTarget.style.display = 'block';
        this.discountCodeMessageTarget.className = 'mt1';
        this.discountCodeIdTarget.value = '';
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      this.discountCodeSpinnerTarget.style.display = 'none';
      // Re-enable button only if package is selected
      this.updateDiscountCodeButtonState();
      this.discountCodeMessageTarget.innerHTML = '<span class="u-textRed">Error validating code. Please try again.</span>';
      this.discountCodeMessageTarget.style.display = 'block';
      this.discountCodeMessageTarget.className = 'mt1';
      this.discountCodeIdTarget.value = '';
    }
  }

  hideDiscountCodeMessage() {
    this.discountCodeMessageTarget.style.display = 'none';
    this.discountCodeSpinnerTarget.style.display = 'none';
  }

  updateDiscountCodeButtonState() {
    // Only update if discount code button exists
    if (!this.hasDiscountCodeButtonTarget || !this.discountCodeButtonTarget) {
      return;
    }

    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    const selectedPackageIdField = document.querySelector('input[name="selected_package_id"]');
    const selectedPackageId = selectedPackageIdField ? selectedPackageIdField.value : '';
    const hasPackageSelected = selectedPackage || (selectedPackageId && selectedPackageId !== '' && selectedPackageId !== '0');

    if (hasPackageSelected) {
      this.discountCodeButtonTarget.disabled = false;
    } else {
      this.discountCodeButtonTarget.disabled = true;
    }
  }

  checkDiscountCodePackage(event) {
    // Check if a package is selected when user types in discount code field
    if (!this.hasDiscountCodeInputTarget || !this.discountCodeInputTarget) {
      return;
    }

    // Update button state first
    this.updateDiscountCodeButtonState();

    const code = this.discountCodeInputTarget.value.trim();
    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    const selectedPackageId = document.querySelector('input[name="selected_package_id"]')?.value;
    const hasPackageSelected = selectedPackage || (selectedPackageId && selectedPackageId !== '');

    if (code && !hasPackageSelected) {
      // Show error message if they're typing but no package is selected
      this.discountCodeMessageTarget.innerHTML = '<span class="u-textRed">Please select a package before applying discount code</span>';
      this.discountCodeMessageTarget.style.display = 'block';
      this.discountCodeMessageTarget.className = 'mt1';
    } else if (!code) {
      // Hide message if they clear the code
      this.hideDiscountCodeMessage();
    } else if (hasPackageSelected) {
      // Hide the error message if they select a package while typing
      // Only hide if it's the "no package" error
      const messageText = this.discountCodeMessageTarget.textContent || '';
      if (messageText.includes('Please select a package')) {
        this.hideDiscountCodeMessage();
      }
    }
  }

}

// Helper function to get meta value
function getMetaValue(name) {
  const element = document.head.querySelector(`meta[name="${name}"]`);
  return element.getAttribute("content");
}

function optionalChaining(valueFn) {
  try {
    return valueFn();
  } catch (e) {
    return undefined;
  }
}

const TOKEN_TO_STATE = Object.freeze({
  "10-and-2": "FL",
  "cantors-nv": "NV",
  "bensons-nc": "NC",
  "odec": "OR"
});

const PERMIT_PATTERNS = Object.freeze({
  // FL: 1 letter + 12 digits
  FL: [/^[A-Za-z]\d{12}$/],

  // NV:
  // 9–10 digits
  // 12 digits
  NV: [/^\d{9}$/, /^\d{10}$/, /^\d{12}$/],

  // NC: 1–12 digits
  NC: [/^\d{1,12}$/],

  // OR:
  // 1 letter + 6 digits (Total 7)
  // 7 digits
  OR: [/^([A-Za-z]\d{6}|\d{7})$/],
});

function isPermitValidForState(state, value) {
  const patterns = PERMIT_PATTERNS[state];
  if (!patterns || !Array.isArray(patterns)) return null;
  return patterns.some((re) => re.test(value));
}
